import React from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload,
  AutoAwesome,
  AutoFixHigh,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../../../api';
import McqOptions from './McqOptions';
import TrueFalseOptions from './TrueFalseOptions';
import FillInBlankEditor from './FillInBlankEditor';
import AnswerVariantsEditor from './AnswerVariantsEditor';
import AnswerRuleEditor from './AnswerRuleEditor';
import FillTypeMarksControls from './FillTypeMarksControls';
import { distributeFixedBlankMarks } from '../../../utils/fillQuestionUtils';
import TableFillEditor, { DEFAULT_GRID } from './TableFillEditor';
import ImageOverlayFillEditor from './ImageOverlayFillEditor';
import ShortAnswerEditor from './ShortAnswerEditor';
import EssayAnswer from './EssayAnswer';
import MatchingEditor from './MatchingEditor';
import OrderingEditor from './OrderingEditor';
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QuillR2Editor,
  QUILL_MODULES as EXPL_QUILL_MODULES,
  QUILL_FORMATS as EXPL_QUILL_FORMATS,
  PASSAGE_QUILL_MODULES,
  PASSAGE_QUILL_FORMATS,
} from '../../../utils/quillQuestionEditorConfig';
import { insertLineNumberMarkers, stripLineNumberMarkers } from '../utils/passageLineExtract';
import HtmlWithLatex from './HtmlWithLatex';
import QuestionPaperLayout from './QuestionPaperLayout';
import InteractiveDiagramEditor, { UnifySharedGridsControl, SharedGridPartsEditor } from './InteractiveDiagramEditor';
import { describeMeasurementAutoDetect } from '../../../utils/measurementQuestionDetect';
import ImportMarkdownViewerDialog from './ImportMarkdownViewerDialog';
import QuestionTypeSelector from './QuestionTypeSelector';
import CategoryTreeSelect from './CategoryTreeSelect';
import TopicTreeSelect from './TopicTreeSelect';
import {
  buildSubQuestionAiPayload,
  applyImprovedSubQuestionPatch,
  mapAnswerDataToSubQuestionPatch,
  sumSubQuestionMarks,
  mergeAiSuggestion,
  withFormattedExplanation,
  AiAcceptSections,
  AiExplanationPreview,
  AI_ACCEPT_DEFAULT,
} from '../QuestionBankForm';

const flattenCategoryTree = (tree) => {
  const flat = [];
  const walk = (nodes) => nodes.forEach((n) => { flat.push(n); if (n.children?.length) walk(n.children); if (n.all_children?.length) walk(n.all_children); });
  walk(Array.isArray(tree) ? tree : []);
  return flat;
};

const findCategoryIdByName = (tree, name) => {
  const flat = flattenCategoryTree(tree);
  const found = flat.find((n) => String(n.name || '').trim().toLowerCase() === name.toLowerCase());
  return found ? String(found.id) : null;
};

const defaultCategoryNameForQuestion = (question) => {
  const src = question.source_type;
  if (src === 'past-paper' || question.import_id) return 'Past Papers';
  if (src === 'mock_exam') return 'Mock Exam';
  if (src === 'course_test') return 'Worksheet';
  if (src === 'ai_generated') return 'AI Generated Papers';
  return 'Past Papers';
};

const normalizeBlanks = (blanks) =>
  Array.isArray(blanks) ? blanks.map((b) => (Array.isArray(b) ? b : [b])) : null;

// Printed lines per swappable row — only meaningful above 1, so anything else is left unsent and the
// grader keeps its one-line-per-row default.
const interchangeableRowSizeValue = (overlayLayout) => {
  const n = parseInt(overlayLayout?.interchangeable_row_size, 10);
  return Number.isInteger(n) && n >= 2 ? n : null;
};

const stripTagsKeepLatex = (html) => (html || '').replace(/<[^>]*>/g, '');

const stripToPlainText = (html) => {
  let text = (html || '').replace(/<[^>]*>/g, '');
  // Decode HTML entities
  if (text.includes('&')) {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    text = el.value;
  }
  // Strip LaTeX math delimiters for readable plain-text preview
  text = text
    .replace(/\$\$([^$]*)\$\$/g, '$1')   // $$...$$ → inner content
    .replace(/\$([^$\n]*)\$/g, '$1')      // $...$ → inner content
    .replace(/\\ /g, ' ')                  // \  (LaTeX space) → regular space
    .replace(/\\[a-zA-Z]+\{([^}]*)\}/g, '$1') // \cmd{content} → content
    .replace(/\\[a-zA-Z]+/g, '');          // bare \cmd → remove
  return text;
};

const toRoman = (n) => {
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ['m','cm','d','cd','c','xc','l','xl','x','ix','v','iv','i'];
  let r = '';
  vals.forEach((v, i) => { while (n >= v) { r += syms[i]; n -= v; } });
  return r;
};

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatExplanationStepwise = (raw) => {
  const clean = String(raw || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\r/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return '';
  const explicitLines = String(raw || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const hasStepMarkers = explicitLines.some((l) => /^(\d+[).]|step\s*\d+[:.)-]?)/i.test(l));
  let steps = [];
  if (hasStepMarkers) {
    steps = explicitLines.map((l) => l.replace(/^(\d+[).]\s*|step\s*\d+[:.)-]?\s*)/i, '').trim());
  } else {
    steps = clean
      .split(/(?:\.\s+|;\s+|,\s+(?=(?:then|next|after that|finally|so)\b))/i)
      .map((s) => (s == null ? '' : String(s)).trim())
      .filter((s) => s.length > 0 && !/^(then|next|after that|finally|so)$/i.test(s));
  }
  if (steps.length === 0) steps = [clean];
  if (steps.length === 1 && steps[0].length > 140) {
    steps = steps[0]
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const items = steps
    .map((s) => escapeHtml(s.replace(/\s+/g, ' ').trim()))
    .filter(Boolean)
    .map((s) => `<li>${s}</li>`)
    .join('');
  return items ? `<ol>${items}</ol>` : '';
};

const normalizeQuillValue = (html) => {
  if (!html) return '';
  const compact = String(html).trim();
  if (compact === '<p><br></p>' || compact === '<p></p>') return '';
  return compact;
};

// Optional drawing/shade answer attached to an image-fill question (e.g. fill the table cells AND
// shade the bar for the missing match). The drawing usually sits on a DIFFERENT image than the fill
// overlay, so the admin picks which attached image it draws on. Persisted as solution_geometry.interactive.
const CombinedDrawingAnswer = ({ interactiveSpec, setInteractiveSpec, attachedImages = [] }) => {
  const enabled = !!interactiveSpec;
  const kind = interactiveSpec?.kind || 'shade';
  const image = interactiveSpec?.image_url || '';
  return (
    <Box sx={{ border: '1px dashed #cfcfcf', borderRadius: 1, p: 1.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={enabled} onChange={(e) => setInteractiveSpec(e.target.checked ? { kind: 'shade', image_url: attachedImages[0] || '' } : null)} />}
        label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Also add a drawing/shade answer (e.g. “draw the bar” on the chart) — graded as an extra mark</Typography>}
      />
      {enabled ? (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Drawing mode</InputLabel>
              <Select label="Drawing mode" value={kind} onChange={(e) => setInteractiveSpec({ ...interactiveSpec, kind: e.target.value })}>
                <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                <MenuItem value="polygon">Draw shape(s) by area (triangle / square / N rectangles)</MenuItem>
                <MenuItem value="segments">Join points (segments)</MenuItem>
                <MenuItem value="marker">Arrow / bar marker (value, or bars, on an axis)</MenuItem>
              </Select>
            </FormControl>
            {attachedImages.length > 1 ? (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Draw on which image</InputLabel>
                <Select label="Draw on which image" value={image} onChange={(e) => setInteractiveSpec({ ...interactiveSpec, image_url: e.target.value })}>
                  {attachedImages.map((u, i) => (
                    <MenuItem key={u} value={u}>Image {i + 1}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
          <InteractiveDiagramEditor
            kind={kind}
            spec={interactiveSpec}
            onChange={setInteractiveSpec}
            imageUrl={image || attachedImages[0] || ''}
          />
        </>
      ) : null}
    </Box>
  );
};

/**
 * Edit a question bank row in-place (PUT /admin/question-bank/:id).
 * Extracted from MockExam for reuse on Paper and other admin flows.
 */
const QuestionBankQuestionEditDialog = ({ question, defaultSchoolYearId, onClose, onSave, saving, createMode = false, createModeLabel = '' }) => {
  const fileInputRef = React.useRef(null);
  const passageDialogQuillRef = React.useRef(null);
  const savedPassageDialogSelectionRef = React.useRef(null);
  const [dialogManualLineNum, setDialogManualLineNum] = React.useState('');
  const [difficulty, setDifficulty] = React.useState(question.difficulty || 'Medium');
  const [isSampleQuestion, setIsSampleQuestion] = React.useState(Boolean(question.is_sample_question));
  const [defaultMarks, setDefaultMarks] = React.useState(question.default_marks ?? 1);
  const [explanation, setExplanation] = React.useState(question.explanation || '');
  const [isImageProcessing, setIsImageProcessing] = React.useState(false);
  const [dragImageIdx, setDragImageIdx] = React.useState(null);
  const [dragOverImageIdx, setDragOverImageIdx] = React.useState(null);
  const [imageUrls, setImageUrls] = React.useState(
    Array.isArray(question.image_urls) && question.image_urls.filter(Boolean).length > 0
      ? question.image_urls.filter(Boolean)
      : question.image_url
        ? [question.image_url]
        : []
  );

  const [options, setOptions] = React.useState(() => {
    const opts = question.options || [];
    // match_text is the right-hand item of a matching pair. Dropping it here left every "Match" box in
    // MatchingEditor blank on open — and, since the same state is what gets saved, a save then wiped the
    // question's pairings. Harmless for every other type (they have no match_text).
    return opts.map((o) => ({ text: o.option_text || o.text || '', match_text: o.match_text || '', image_url: o.image_url || null }));
  });
  const [correctOptionIndex, setCorrectOptionIndex] = React.useState(() => {
    const opts = question.options || [];
    const idx = opts.findIndex((o) => o.is_correct);
    return idx >= 0 ? idx : 0;
  });
  const [correctOptionIndices, setCorrectOptionIndices] = React.useState(() => {
    const opts = question.options || [];
    return opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []);
  });
  const [trueFalseAnswer, setTrueFalseAnswer] = React.useState(() => {
    const opts = question.options || [];
    const trueOpt = opts.find((o) => o.option_text === 'True' || o.text === 'True');
    return trueOpt?.is_correct ?? true;
  });
  const [blanks, setBlanks] = React.useState(
    normalizeBlanks(question.answer?.correct_blanks) || (question.type === 'multi-fill' ? [[''], ['']] : [['']])
  );
  const [blankLabels, setBlankLabels] = React.useState(question.answer?.blank_labels || []);
  const [blankMarks, setBlankMarks] = React.useState(question.answer?.blank_marks || []);
  // Complete alternative answers (one value per blank). See AnswerVariantsEditor — correct_blanks alone
  // cannot say which values belong together, so a combination question accepts a mix of two answers.
  const [answerVariants, setAnswerVariants] = React.useState(question.answer?.answer_variants || null);
  // Open-ended answer rule — see AnswerRuleEditor. Describes the accepting SET ("any multiple of
  // 2001") where correct_blanks / correct_answer_text can only ever store one example of it.
  const [answerRule, setAnswerRule] = React.useState(question.answer?.answer_rule || null);
  const [blankLabelPositions, setBlankLabelPositions] = React.useState(
    question.answer?.blank_label_positions || question.answer_data?.blank_label_positions || []
  );
  const [fillMarksMode, setFillMarksMode] = React.useState(
    question.answer_data?.marks_mode === 'fixed' ? 'fixed' : 'sum'
  );
  const [fillFixedTotal, setFillFixedTotal] = React.useState(
    question.answer_data?.marks_mode === 'fixed'
      ? (question.answer_data?.marks_total ?? question.default_marks ?? 4)
      : 4
  );
  const [tableLayout, setTableLayout] = React.useState(
    question.answer?.table_layout?.grid ? question.answer.table_layout : DEFAULT_GRID()
  );
  const [overlayLayout, setOverlayLayout] = React.useState(
    (question.answer?.overlay_layout?.image_url || question.answer?.overlay_layout?.mode || question.answer?.overlay_layout?.rows_interchangeable || question.answer?.overlay_layout?.blanks_interchangeable) ? question.answer.overlay_layout : { image_url: '', blanks: [] }
  );
  const [shortAnswer, setShortAnswer] = React.useState(question.answer?.correct_answer_text || '');
  // "Must be written exactly like the mark scheme": turns every fuzzy tier and the AI grader off for this
  // answer. For mark schemes where the notation IS the answer (an expression, a formula, a given form).
  const [exactMatch, setExactMatch] = React.useState(!!question.answer?.exact_match);
  // Free prose ("write two other things which are the same about all 4 shapes") is graded by meaning —
  // no exact/fuzzy tier can match it, so the whole answer goes to the AI grader.
  const [semanticMatch, setSemanticMatch] = React.useState(!!question.answer?.semantic_match);
  const [modelAnswer, setModelAnswer] = React.useState(question.answer?.correct_answer_text || '');
  const [correctSolutionImageUrl, setCorrectSolutionImageUrl] = React.useState(question.answer?.correct_solution_image_url || '');
  const [generatingAnswerImage, setGeneratingAnswerImage] = React.useState(false);
  const [interactiveSpec, setInteractiveSpec] = React.useState(
    (question.answer?.solution_geometry && question.answer.solution_geometry.interactive) || null
  );
  const drawingAnswerMode = interactiveSpec?.kind || 'free';
  const [forceMeasurementTool, setForceMeasurementTool] = React.useState(
    (question.answer?.solution_geometry && question.answer.solution_geometry.force_measurement_tool)
    || (question.answer?.solution_geometry?.interactive?.force_tool)
    || 'auto'
  );
  const [questionText, setQuestionText] = React.useState(question.question_text || '');
  const [sectionTitle, setSectionTitle] = React.useState(question.section_title ?? '');
  const [sectionInstructions, setSectionInstructions] = React.useState(question.section_instructions ?? '');

  // Grouped question sub-questions
  const buildSqState = (sq) => {
    const opts = sq.options || [];
    const ans = sq.answer || {};
    return {
      id: sq.id,
      question_text: sq.question_text || '',
      type: sq.type || 'multiple-choice',
      default_marks: sq.default_marks ?? 1,
      options: opts.map((o) => ({ text: o.option_text || o.text || '', match_text: o.match_text || '', image_url: o.image_url || null })),
      correct_option_index: Math.max(0, opts.findIndex((o) => o.is_correct)),
      correct_option_indices: opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []),
      trueFalseAnswer: (opts.find((o) => (o.option_text || o.text) === 'True')?.is_correct) ?? true,
      image_url: sq.image_url || '',
      image_description: sq.image_description ?? '',
      blanks: normalizeBlanks(ans.correct_blanks) || (sq.type === 'multi-fill' ? [[''], ['']] : [['']]),
      answerVariants: ans.answer_variants || null,
      answerRule: ans.answer_rule || null,
      blankLabels: ans.blank_labels || [],
      blankMarks: ans.blank_marks || [],
      blankLabelPositions: ans.blank_label_positions || [],
      tableLayout: ans.table_layout?.grid ? ans.table_layout : DEFAULT_GRID(),
      overlayLayout: (ans.overlay_layout?.image_url || ans.overlay_layout?.mode || ans.overlay_layout?.rows_interchangeable || ans.overlay_layout?.blanks_interchangeable) ? ans.overlay_layout : { image_url: '', blanks: [] },
      marksMode: ans.marks_mode === 'fixed' ? 'fixed' : 'sum',
      fixedTotal: ans.marks_total ?? (sq.default_marks ?? 1),
      exactMatch: !!ans.exact_match,
      semanticMatch: !!ans.semantic_match,
      shortAnswer: ans.correct_answer_text || '',
      modelAnswer: ans.correct_answer_text || '',
      correctSolutionImageUrl: ans.correct_solution_image_url || '',
      interactiveSpec: (ans.solution_geometry && ans.solution_geometry.interactive) || null,
      explanation: sq.explanation || '',
      hide_shared_context: !!sq.hide_shared_context,
      mcq_recommended: !!sq.mcq_recommended,
      mcq_recommended_note: sq.mcq_recommended_note || '',
      sub_questions: (sq.sub_questions || []).map((ssq) => {
        const sOpts = ssq.options || [];
        const sAns = ssq.answer || {};
        return {
          id: ssq.id,
          question_text: ssq.question_text || '',
          type: ssq.type || 'short-answer',
          default_marks: ssq.default_marks ?? 1,
          options: sOpts.map((o) => ({ text: o.option_text || o.text || '', match_text: o.match_text || '', image_url: o.image_url || null })),
          correct_option_index: Math.max(0, sOpts.findIndex((o) => o.is_correct)),
          correct_option_indices: sOpts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []),
          trueFalseAnswer: (sOpts.find((o) => (o.option_text || o.text) === 'True')?.is_correct) ?? true,
          image_url: ssq.image_url || '',
          image_description: ssq.image_description ?? '',
          blanks: normalizeBlanks(sAns.correct_blanks) || (ssq.type === 'multi-fill' ? [[''], ['']] : [['']]),
          answerVariants: sAns.answer_variants || null,
          answerRule: sAns.answer_rule || null,
          blankLabels: sAns.blank_labels || [],
          blankMarks: sAns.blank_marks || [],
          blankLabelPositions: sAns.blank_label_positions || [],
          tableLayout: sAns.table_layout?.grid ? sAns.table_layout : DEFAULT_GRID(),
          overlayLayout: (sAns.overlay_layout?.image_url || sAns.overlay_layout?.mode || sAns.overlay_layout?.rows_interchangeable || sAns.overlay_layout?.blanks_interchangeable) ? sAns.overlay_layout : { image_url: '', blanks: [] },
          marksMode: sAns.marks_mode === 'fixed' ? 'fixed' : 'sum',
          fixedTotal: sAns.marks_total ?? (ssq.default_marks ?? 1),
          exactMatch: !!sAns.exact_match,
          semanticMatch: !!sAns.semantic_match,
          shortAnswer: sAns.correct_answer_text || '',
          modelAnswer: sAns.correct_answer_text || '',
          explanation: ssq.explanation || '',
        };
      }),
    };
  };

  const newSqState = (type = 'short-answer') => ({
    question_text: '',
    type,
    default_marks: 1,
    options: [{ text: '', image_url: null }, { text: '', image_url: null }],
    correct_option_index: 0,
    correct_option_indices: [],
    trueFalseAnswer: true,
    image_url: '',
    image_description: '',
    blanks: type === 'multi-fill' ? [[''], ['']] : [['']],
    answerVariants: null,
    answerRule: null,
    blankLabels: [],
    blankMarks: [],
    blankLabelPositions: [],
    tableLayout: DEFAULT_GRID(),
    overlayLayout: { image_url: '', blanks: [] },
    marksMode: 'sum',
    fixedTotal: 1,
    shortAnswer: '',
    modelAnswer: '',
    correctSolutionImageUrl: '',
    interactiveSpec: null,
    explanation: '',
    hide_shared_context: false,
    mcq_recommended: false,
    mcq_recommended_note: '',
    sub_questions: [],
  });

  const [localType, setLocalType] = React.useState(question.type);

  const [subQuestions, setSubQuestions] = React.useState(() =>
    (question.sub_questions || []).map(buildSqState)
  );
  const [expandedSq, setExpandedSq] = React.useState(null);
  const [subLabelType, setSubLabelType] = React.useState(question.sub_label_type || 'letter');
  const [groupedMarksMode, setGroupedMarksMode] = React.useState(
    question.answer_data?.marks_mode === 'fixed' ? 'fixed' : 'sum'
  );
  const [groupedExtend, setGroupedExtend] = React.useState(!!question.answer_data?.extend_parts);
  const [groupedFixedTotal, setGroupedFixedTotal] = React.useState(
    question.answer_data?.marks_mode === 'fixed'
      ? (question.answer_data?.marks_total ?? question.default_marks ?? 4)
      : 4
  );

  const [imageDescription, setImageDescription] = React.useState(question.image_description ?? '');
  const [subjectId, setSubjectId] = React.useState(question.subject_id || null);
  // Kept so a second click can correct the figure instead of redrawing from a blank slate.
  const [aiVisualSpec, setAiVisualSpec] = React.useState(null);
  const [topicIds, setTopicIds] = React.useState(
    Array.isArray(question.topic_ids) ? question.topic_ids.map((id) => Number(id)) : []
  );
  const [topicCatalog, setTopicCatalog] = React.useState([]);
  const [schoolYears, setSchoolYears] = React.useState([]);
  const [topicYearId, setTopicYearId] = React.useState('');
  const [passageType, setPassageType] = React.useState(question.passage_type || '');
  const [mcqRecommended, setMcqRecommended] = React.useState(!!question.mcq_recommended);
  const [mcqRecommendedNote, setMcqRecommendedNote] = React.useState(question.mcq_recommended_note || '');
  const [questionLivePreview, setQuestionLivePreview] = React.useState(true);
  const [imageDropActive, setImageDropActive] = React.useState(false);
  const [generatingExplanation, setGeneratingExplanation] = React.useState(false);
  const [improving, setImproving] = React.useState(false);
  const [generatingAnswer, setGeneratingAnswer] = React.useState(false);
  const [generatingDiagram, setGeneratingDiagram] = React.useState(false);
  const [aiImproveOpen, setAiImproveOpen] = React.useState(false);
  const [aiOriginal, setAiOriginal] = React.useState(null);
  const [aiSuggestion, setAiSuggestion] = React.useState(null);
  const [subAiImproveOpen, setSubAiImproveOpen] = React.useState(false);
  const [subAiImproveIdx, setSubAiImproveIdx] = React.useState(null);
  const [subAiOriginal, setSubAiOriginal] = React.useState(null);
  const [subAiSuggestion, setSubAiSuggestion] = React.useState(null);
  const [aiAccept, setAiAccept] = React.useState(AI_ACCEPT_DEFAULT);
  const [subAiAccept, setSubAiAccept] = React.useState(AI_ACCEPT_DEFAULT);
  const [subSqLoading, setSubSqLoading] = React.useState(null);
  const [subSqImageUploading, setSubSqImageUploading] = React.useState(null);
  const [markdownDialogOpen, setMarkdownDialogOpen] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState(question.category_id ? String(question.category_id) : '');
  const [categories, setCategories] = React.useState([]);
  const [subjects, setSubjects] = React.useState([]);

  const importId = question.import_id ?? null;

  React.useEffect(() => {
    api.get('/admin/question-bank/exam-subjects')
      .then((res) => setSubjects(res.data?.data || []))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    api.get('/admin/mock-exam/category-tree').then((res) => {
      const tree = res.data?.data || [];
      setCategories(tree);
      if (!question.category_id) {
        const targetName = defaultCategoryNameForQuestion(question);
        const id = findCategoryIdByName(tree, targetName);
        if (id) setCategoryId(id);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setSubjectId(question.subject_id || null);
    setTopicIds(Array.isArray(question.topic_ids) ? question.topic_ids.map((id) => Number(id)) : []);
    setPassageType(question.passage_type || '');
  }, [question.id, question.subject_id, question.topic_ids, question.passage_type]);

  React.useEffect(() => {
    setIsSampleQuestion(Boolean(question.is_sample_question));
  }, [question.id, question.is_sample_question]);

  React.useEffect(() => {
    setMcqRecommended(Boolean(question.mcq_recommended));
    setMcqRecommendedNote(question.mcq_recommended_note || '');
  }, [question.id, question.mcq_recommended, question.mcq_recommended_note]);

  React.useEffect(() => {
    api.get('/admin/master-form/school-years')
      .then((res) => {
        const rows = res.data?.data || [];
        setSchoolYears(rows);
        const preferred = question.school_year_id ?? defaultSchoolYearId;
        if (preferred) {
          const match = rows.find((y) => String(y.id) === String(preferred));
          if (match) { setTopicYearId(String(match.id)); return; }
        }
        const y5 = rows.find((y) => String(y.name || '').toLowerCase().replace(/\s+/g, '') === 'year5');
        if (y5) setTopicYearId(String(y5.id));
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!subjectId) {
      setTopicCatalog([]);
      return;
    }
    let cancelled = false;
    api.get('/admin/question-bank/topics-by-subject', { params: { subject_id: subjectId, include_counts: 1 } })
      .then((res) => {
        if (!cancelled) setTopicCatalog(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setTopicCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [subjectId]);

  const handleDialogInsertLineNumbers = () => {
    const numbered = insertLineNumberMarkers(questionText || '');
    setQuestionText(numbered);
    toast.success('Line numbers inserted. Edit the {L…} markers if needed.');
  };

  const handleDialogClearLineNumbers = () => {
    setQuestionText(stripLineNumberMarkers(questionText || ''));
  };

  const handleDialogInsertManualLineNum = () => {
    const num = parseInt(dialogManualLineNum, 10);
    if (!num || num < 1) return;
    const marker = `{L${num}} `;
    const editor = passageDialogQuillRef.current?.getEditor?.();
    if (editor) {
      const saved = savedPassageDialogSelectionRef.current;
      const insertAt = saved ? saved.index : editor.getLength() - 1;
      editor.insertText(insertAt, marker, 'user');
      editor.setSelection(insertAt + marker.length);
      editor.focus();
    } else {
      setQuestionText((questionText || '') + marker);
    }
    setDialogManualLineNum('');
  };

  const uploadStemImageFile = async (file) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setIsImageProcessing(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/admin/question-bank/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.data?.image_url || '';
      if (newUrl) setImageUrls((prev) => [...prev, newUrl]);
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setIsImageProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadStemImageFile(file);
  };

  const handleRemoveImage = async (urlToRemove) => {
    setIsImageProcessing(true);
    setImageUrls((prev) => prev.filter((u) => u !== urlToRemove));
    try {
      await api.post('/admin/question-bank/delete-image', { url: urlToRemove });
    } catch {
      setImageUrls((prev) => [...prev, urlToRemove]);
      toast.error('Failed to remove image');
    } finally {
      setIsImageProcessing(false);
    }
  };

  const subSqFileInputRefs = React.useRef({});
  const [subSqDragOver, setSubSqDragOver] = React.useState(null);

  const handleSubSqImageUpload = async (idx, file) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    setSubSqImageUploading(idx);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/admin/question-bank/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.data?.image_url || '';
      if (newUrl) {
        setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, image_url: newUrl } : s)));
      }
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setSubSqImageUploading(null);
      const inp = subSqFileInputRefs.current[idx];
      if (inp) inp.value = '';
    }
  };

  const triggerSubSqImageUpload = (idx) => {
    subSqFileInputRefs.current[idx]?.click();
  };

  const handleSubSqImageRemove = (idx) => {
    setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, image_url: '' } : s)));
  };

  const handleSqTypeChange = (idx, newType) => {
    const tableDefaults = newType === 'table'
      ? {
          blanks: Array.from({ length: 9 }, () => ['']),
          blankMarks: Array.from({ length: 9 }, () => 1),
          tableLayout: DEFAULT_GRID(),
        }
      : {};
    const imageFillDefaults = newType === 'image-fill'
      ? {
          blanks: [['']],
          blankMarks: [1],
          overlayLayout: { image_url: '', blanks: [] },
        }
      : {};
    setSubQuestions((prev) =>
      prev.map((s, i) =>
        i !== idx ? s : {
          ...s,
          type: newType,
          options: [{ text: '', image_url: null }, { text: '', image_url: null }],
          correct_option_index: 0,
          correct_option_indices: [],
          trueFalseAnswer: true,
          blanks: newType === 'multi-fill' ? [[''], ['']] : [['']],
          blankLabels: [],
          blankMarks: [],
          blankLabelPositions: [],
          shortAnswer: '',
          modelAnswer: '',
          ...tableDefaults,
          ...imageFillDefaults,
        }
      )
    );
  };

  const handleLocalTypeChange = (newType) => {
    if (localType === 'grouped' && newType !== 'grouped') {
      if (!window.confirm('Changing type will remove all sub-questions. Continue?')) return;
      setSubQuestions([]);
    }
    if (newType === 'grouped' && subQuestions.length === 0) {
      setSubQuestions([newSqState()]);
    }
    setOptions([{ text: '', image_url: null }, { text: '', image_url: null }]);
    setCorrectOptionIndex(0);
    setCorrectOptionIndices([]);
    setTrueFalseAnswer(true);
    setBlanks(newType === 'multi-fill' ? [[''], ['']] : newType === 'table' ? Array.from({ length: 9 }, () => ['']) : [['']]);
    setBlankLabels([]);
    setBlankMarks(newType === 'table' ? Array.from({ length: 9 }, () => 1) : []);
    setTableLayout(DEFAULT_GRID());
    setOverlayLayout({ image_url: '', blanks: [] });
    setShortAnswer('');
    setModelAnswer('');
    setLocalType(newType);
  };

  const handleAddSubQuestion = () => {
    setSubQuestions((prev) => {
      const next = [...prev, newSqState()];
      setExpandedSq(next.length - 1);
      return next;
    });
  };

  const handleDeleteSubQuestion = (idx) => {
    if (!window.confirm('Remove this sub-question?')) return;
    setSubQuestions((prev) => prev.filter((_, i) => i !== idx));
    setExpandedSq((cur) => (cur === idx ? null : cur != null && cur > idx ? cur - 1 : cur));
  };

  const handleConvertToGrouped = () => {
    const seed = newSqState(localType === 'grouped' ? 'short-answer' : localType);
    seed.question_text = questionText || '';
    if (imageUrls[0]) seed.image_url = imageUrls[0];
    setSubQuestions((prev) => (prev.length > 0 ? prev : [seed]));
    setLocalType('grouped');
    setExpandedSq(0);
  };

  const handleImageReorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setImageUrls((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  // "Fixed total marks" means the question's mark is SPLIT across its blanks, so the stored per-blank
  // marks have to be that split — leaving the editor's 1-per-blank values in place made every surface
  // that sums blank_marks re-derive 8 marks for a 4-mark table on the next load.
  const fillTypeBlankMarksPayload = (blankCount) => {
    if (fillMarksMode === 'fixed') {
      const split = distributeFixedBlankMarks(parseInt(fillFixedTotal, 10) || 1, blankCount);
      return split.length > 0 ? { blank_marks: split } : {};
    }
    return blankMarks.length > 0 ? { blank_marks: blankMarks } : {};
  };

  const subFillBlankMarksPayload = (sq) => {
    if (sq.marksMode === 'fixed') {
      const split = distributeFixedBlankMarks(parseInt(sq.fixedTotal, 10) || 1, sq.blanks?.length ?? 0);
      return split.length > 0 ? { blank_marks: split } : {};
    }
    return sq.blankMarks?.length > 0 ? { blank_marks: sq.blankMarks } : {};
  };

  const buildAiQuestionPayload = () => {
    const base = {
      type: question.type,
      question_text: questionText,
      difficulty,
      default_marks: parseInt(defaultMarks, 10) || 1,
      topic: question.topic || null,
      image_description: imageDescription || null,
      image_url: imageUrls[0] || null,
      image_urls: imageUrls.length > 0 ? imageUrls : null,
      section_title: sectionTitle || null,
      section_instructions: sectionInstructions || null,
      explanation: explanation || null,
    };
    const t = question.type;
    switch (t) {
      case 'multiple-choice': {
        const opts = options.map((o, i) => ({
          text: o.text || '',
          option_text: o.text || '',
          image_url: o.image_url || null,
          is_correct: i === correctOptionIndex,
        }));
        return { ...base, options: opts, answer_data: { correct_options: [correctOptionIndex] } };
      }
      case 'multiple-select': {
        const opts = options.map((o, i) => ({
          text: o.text || '',
          option_text: o.text || '',
          image_url: o.image_url || null,
          is_correct: correctOptionIndices.includes(i),
        }));
        return { ...base, options: opts, answer_data: { correct_options: [...correctOptionIndices] } };
      }
      case 'true-false': {
        const opts = [
          { text: 'True', option_text: 'True', is_correct: trueFalseAnswer },
          { text: 'False', option_text: 'False', is_correct: !trueFalseAnswer },
        ];
        return { ...base, options: opts, answer_data: { correct_options: [trueFalseAnswer ? 0 : 1] } };
      }
      case 'fill-in-the-blank':
      case 'multi-fill':
        return {
          ...base,
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
            ...(fillTypeBlankMarksPayload(blanks.length)),
            ...(blankLabelPositions.length > 0 ? { blank_label_positions: blankLabelPositions } : {}),
            ...(fillMarksMode === 'fixed' ? { marks_mode: 'fixed', marks_total: parseInt(fillFixedTotal, 10) || 1 } : {}),
          },
        };
      case 'table':
        return {
          ...base,
          type: 'table',
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
            ...(fillTypeBlankMarksPayload(blanks.length)),
            ...(tableLayout?.grid?.length > 0 ? { table_layout: tableLayout } : {}),
            ...(fillMarksMode === 'fixed' ? { marks_mode: 'fixed', marks_total: parseInt(fillFixedTotal, 10) || 1 } : {}),
          },
        };
      case 'image-fill':
        return {
          ...base,
          type: 'image-fill',
          answer_data: {
            correct_blanks: blanks,
            ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
            ...(fillTypeBlankMarksPayload(blanks.length)),
            ...((overlayLayout?.image_url || overlayLayout?.mode === 'inline') ? { overlay_layout: overlayLayout } : {}),
            ...(fillMarksMode === 'fixed' ? { marks_mode: 'fixed', marks_total: parseInt(fillFixedTotal, 10) || 1 } : {}),
          },
        };
      case 'short-answer':
        return { ...base, answer_data: { correct_answer_text: shortAnswer } };
      case 'essay':
        return { ...base, answer_data: { correct_answer_text: modelAnswer, model_answer: modelAnswer } };
      case 'matching':
        return {
          ...base,
          options: options.map((o) => ({
            text: o.text || '',
            option_text: o.text || '',
            image_url: o.image_url || null,
            match_text: o.match_text || '',
            is_correct: true,
          })),
          answer_data: {},
        };
      case 'ordering':
        return {
          ...base,
          options: options.map((o) => ({
            text: o.text || '',
            option_text: o.text || '',
            image_url: o.image_url || null,
            is_correct: true,
          })),
          answer_data: {},
        };
      default:
        return base;
    }
  };

  const applyImprovedQuestionToDialog = (imp) => {
    if (!imp || typeof imp !== 'object') return;
    if (imp.question_text != null) setQuestionText(imp.question_text);
    if (imp.difficulty) setDifficulty(imp.difficulty);
    if (imp.default_marks != null) setDefaultMarks(imp.default_marks);
    if (imp.image_description != null) setImageDescription(imp.image_description || '');
    if (imp.explanation != null) setExplanation(imp.explanation || '');
    if (imp.section_title !== undefined) setSectionTitle(imp.section_title || '');
    if (imp.section_instructions !== undefined) setSectionInstructions(imp.section_instructions || '');
    const urls =
      Array.isArray(imp.image_urls) && imp.image_urls.filter(Boolean).length > 0
        ? imp.image_urls.filter(Boolean)
        : imp.image_url
          ? [imp.image_url]
          : null;
    if (urls && urls.length > 0) setImageUrls(urls);

    const ad = imp.answer_data || {};
    const opts = imp.options || [];
    const impType = imp.type || question.type;

    if (impType === 'multiple-choice' && opts.length > 0) {
      setOptions(opts.map((o) => ({ text: o.text || o.option_text || '', image_url: o.image_url || null })));
      const ci = opts.findIndex((o) => o.is_correct);
      if (ci >= 0) setCorrectOptionIndex(ci);
      else if (Array.isArray(ad.correct_options) && ad.correct_options.length > 0) setCorrectOptionIndex(ad.correct_options[0]);
    }
    if (impType === 'multiple-select' && opts.length > 0) {
      setOptions(opts.map((o) => ({ text: o.text || o.option_text || '', image_url: o.image_url || null })));
      const idxs = opts.reduce((acc, o, i) => (o.is_correct ? [...acc, i] : acc), []);
      if (idxs.length > 0) setCorrectOptionIndices(idxs);
      else if (Array.isArray(ad.correct_options)) setCorrectOptionIndices(ad.correct_options);
    }
    if (impType === 'true-false') {
      const trueOpt = opts.find((o) => (o.text || o.option_text) === 'True');
      if (trueOpt && trueOpt.is_correct !== undefined) setTrueFalseAnswer(!!trueOpt.is_correct);
    }
    if (impType === 'fill-in-the-blank' || impType === 'multi-fill') {
      if (ad.correct_blanks?.length > 0) setBlanks(normalizeBlanks(ad.correct_blanks) || [['']]);
      if (ad.blank_labels?.length > 0) setBlankLabels(ad.blank_labels);
      if (ad.blank_marks?.length > 0) setBlankMarks(ad.blank_marks);
    }
    if (impType === 'table') {
      if (ad.correct_blanks?.length > 0) setBlanks(normalizeBlanks(ad.correct_blanks) || [['']]);
      if (ad.blank_labels?.length > 0) setBlankLabels(ad.blank_labels);
      if (ad.blank_marks?.length > 0) setBlankMarks(ad.blank_marks);
      if (ad.table_layout?.grid) setTableLayout(ad.table_layout);
    }
    if (impType === 'image-fill') {
      if (ad.correct_blanks?.length > 0) setBlanks(normalizeBlanks(ad.correct_blanks) || [['']]);
      if (ad.blank_labels?.length > 0) setBlankLabels(ad.blank_labels);
      if (ad.blank_marks?.length > 0) setBlankMarks(ad.blank_marks);
      if (ad.overlay_layout?.image_url || ad.overlay_layout?.mode) setOverlayLayout(ad.overlay_layout);
    }
    if (impType === 'short-answer' && ad.correct_answer_text) setShortAnswer(ad.correct_answer_text);
    if (impType === 'essay' && ad.correct_answer_text) setModelAnswer(ad.correct_answer_text);
    if (impType === 'matching' && opts.length > 0) {
      setOptions(
        opts.map((o) => ({
          text: o.text || o.option_text || '',
          match_text: o.match_text || '',
          image_url: o.image_url || null,
        }))
      );
    }
    if (impType === 'ordering' && opts.length > 0) {
      setOptions(opts.map((o) => ({ text: o.text || o.option_text || '', image_url: o.image_url || null })));
    }
  };

  const handleSubSqGenerateExplanation = async (idx, sq) => {
    if (!question.id) return;
    setSubSqLoading({ idx, kind: 'expl' });
    try {
      const res = await api.post(`/admin/question-bank/${question.id}/generate-explanation`, {
        question: buildSubQuestionAiPayload(sq, { difficulty, topic: question.topic || null }),
      });
      const text = res.data?.data?.explanation;
      if (text) {
        setSubQuestions((prev) =>
          prev.map((s, i) => {
            if (i !== idx) return s;
            const prevExp = s.explanation || '';
            const merged =
              /<img\s/i.test(prevExp) && prevExp.replace(/<[^>]*>/g, '').trim()
                ? `${prevExp}<p><br></p>${formatExplanationStepwise(text)}`
                : formatExplanationStepwise(text);
            return { ...s, explanation: merged };
          })
        );
      }
      toast.success('Explanation generated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate explanation');
    } finally {
      setSubSqLoading(null);
    }
  };

  const handleSubSqAiImprove = async (idx, sq) => {
    if (!question.id) return;
    setSubSqLoading({ idx, kind: 'improve' });
    try {
      const payload = buildSubQuestionAiPayload(sq, { difficulty, topic: question.topic || null });
      const res = await api.post(`/admin/question-bank/${question.id}/ai-improve-question`, {
        question: payload,
      });
      setSubAiOriginal(payload);
      setSubAiSuggestion(withFormattedExplanation(res.data?.data?.improved_question));
      setSubAiImproveIdx(idx);
      setSubAiAccept(AI_ACCEPT_DEFAULT);
      setSubAiImproveOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI improvement failed');
    } finally {
      setSubSqLoading(null);
    }
  };

  const handleSubSqGenerateAnswerImage = async (idx, sq) => {
    if (!sq.id) {
      toast.info('Save the question so this sub-question has an ID, then generate the answer diagram.');
      return;
    }
    setSubSqLoading({ idx, kind: 'answer-image' });
    try {
      const res = await api.post(`/admin/question-bank/${sq.id}/generate-answer-image`, {
        image_url: sq.image_url || null,
      });
      const url = res.data?.data?.correct_solution_image_url || '';
      if (url) {
        setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, correctSolutionImageUrl: url } : s)));
        toast.success('Answer diagram generated — review before saving.');
      } else {
        toast.error('No answer image was returned.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate answer diagram.');
    } finally {
      setSubSqLoading(null);
    }
  };

  const handleSubSqGenerateAnswer = async (idx, sq) => {
    if (!sq.id) {
      toast.info('Save the question so this sub-question has an ID, then try again.');
      return;
    }
    setSubSqLoading({ idx, kind: 'answer' });
    try {
      const res = await api.post(`/admin/question-bank/${sq.id}/generate-answer`, {
        question: buildSubQuestionAiPayload(sq, { difficulty, topic: question.topic || null }),
      });
      const answerData = res.data?.data?.answer_data || {};
      const patch = mapAnswerDataToSubQuestionPatch(answerData, sq.type, sq);
      if (Object.keys(patch).length > 0) {
        setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
      }
      toast.success('Answer generated — review before saving');
    } catch {
      toast.error('Failed to generate answer');
    } finally {
      setSubSqLoading(null);
    }
  };

  const handleGenerateExplanationAi = async () => {
    if (question.type === 'grouped') return;
    setGeneratingExplanation(true);
    try {
      const res = await api.post(`/admin/question-bank/${question.id}/generate-explanation`, {
        question: buildAiQuestionPayload(),
      });
      const text = res.data?.data?.explanation;
      if (text) {
        const prev = explanation || '';
        if (/<img\s/i.test(prev) && prev.replace(/<[^>]*>/g, '').trim()) {
          setExplanation(`${prev}<p><br></p>${formatExplanationStepwise(text)}`);
        } else {
          setExplanation(formatExplanationStepwise(text));
        }
      }
      toast.success('Explanation generated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate explanation');
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const handleAiImproveClick = async () => {
    if (question.type === 'grouped') return;
    setImproving(true);
    try {
      const payload = buildAiQuestionPayload();
      const res = await api.post(`/admin/question-bank/${question.id}/ai-improve-question`, {
        question: payload,
      });
      setAiOriginal(payload);
      setAiSuggestion(withFormattedExplanation(res.data?.data?.improved_question));
      setAiAccept(AI_ACCEPT_DEFAULT);
      setAiImproveOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'AI improvement failed');
    } finally {
      setImproving(false);
    }
  };

  const handleGenerateAnswerClick = async () => {
    setGeneratingAnswer(true);
    try {
      const res = await api.post(`/admin/question-bank/${question.id}/generate-answer`, {
        question: buildAiQuestionPayload(),
      });
      const answerData = res.data?.data?.answer_data || {};
      const t = question.type;
      if (t === 'multiple-choice' && answerData.correct_options?.length > 0) {
        setCorrectOptionIndex(answerData.correct_options[0]);
      }
      if (t === 'multiple-select' && answerData.correct_options?.length > 0) {
        setCorrectOptionIndices(answerData.correct_options);
      }
      if (t === 'true-false' && answerData.correct_options !== undefined) {
        setTrueFalseAnswer((answerData.correct_options?.[0] ?? 0) === 0);
      }
      if ((t === 'fill-in-the-blank' || t === 'multi-fill') && answerData.correct_blanks?.length > 0) {
        setBlanks(normalizeBlanks(answerData.correct_blanks) || [['']]);
        if (answerData.blank_labels?.length > 0) setBlankLabels(answerData.blank_labels);
        if (answerData.blank_marks?.length > 0) setBlankMarks(answerData.blank_marks);
      }
      if (t === 'table' && answerData.correct_blanks?.length > 0) {
        setBlanks(normalizeBlanks(answerData.correct_blanks) || [['']]);
        if (answerData.blank_labels?.length > 0) setBlankLabels(answerData.blank_labels);
        if (answerData.blank_marks?.length > 0) setBlankMarks(answerData.blank_marks);
        if (answerData.table_layout?.grid) setTableLayout(answerData.table_layout);
      }
      if (t === 'image-fill' && answerData.correct_blanks?.length > 0) {
        setBlanks(normalizeBlanks(answerData.correct_blanks) || [['']]);
        if (answerData.blank_labels?.length > 0) setBlankLabels(answerData.blank_labels);
        if (answerData.blank_marks?.length > 0) setBlankMarks(answerData.blank_marks);
        if (answerData.overlay_layout?.image_url || answerData.overlay_layout?.mode) setOverlayLayout(answerData.overlay_layout);
      }
      if (t === 'short-answer' && answerData.correct_answer_text) {
        setShortAnswer(answerData.correct_answer_text);
      }
      if (t === 'essay' && answerData.correct_answer_text) {
        setModelAnswer(answerData.correct_answer_text);
      }
      toast.success('Answer generated — review before saving');
    } catch {
      toast.error('Failed to generate answer');
    } finally {
      setGeneratingAnswer(false);
    }
  };

  const handleGenerateDiagramClick = async () => {
    const optionTexts = localType === 'multiple-choice'
      ? options.map((o) => String(o.text || '').trim()).filter(Boolean)
      : [];
    setGeneratingDiagram(true);
    try {
      // No id yet in create mode — the id-less route renders without persisting.
      const url = question?.id
        ? `/admin/question-bank/${question.id}/generate-diagram`
        : '/admin/question-bank/generate-diagram';
      // The mark scheme matters: a "what does the scale show?" figure is drawn to the answer.
      const correctAnswerText = localType === 'multiple-choice'
        ? String(options[correctOptionIndex]?.text || '').trim()
        : String(shortAnswer || '').trim();
      const res = await api.post(url, {
        image_description: imageDescription || '',
        question_text: questionText || '',
        subject_id: subjectId || null,
        visual: aiVisualSpec,
        answer: correctAnswerText || null,
        options: optionTexts.length >= 2 ? optionTexts : null,
        include_option_images: optionTexts.length >= 2,
        // The topic names the figure when the description never does — "Data Handling ▸ Pictograms"
        // routes a "horizontal chart" description to the pictogram renderer instead of a bar chart.
        topic_ids: topicIds,
        ...(blankLabels.length > 0 ? { blank_labels: blankLabels } : {}),
      });
      const data = res.data?.data || {};
      const newUrl = data.image_url || '';
      if (newUrl) {
        setImageUrls((prev) => {
          const filtered = prev.filter((u) => u !== newUrl);
          return [newUrl, ...filtered];
        });
      }
      setAiVisualSpec(data.visual || null);
      if (data.image_description && !imageDescription) {
        setImageDescription(data.image_description);
      }
      if (data.option_images) {
        setOptions((prev) =>
          prev.map((o) =>
            data.option_images[String(o.text || '').trim()]
              ? { ...o, image_url: data.option_images[String(o.text || '').trim()] }
              : o
          )
        );
        toast.success(`Generated images for ${Object.keys(data.option_images).length} answer option(s).`);
      }
      if (!newUrl) return;
      if (data.verified === false) {
        toast.warning('Diagram generated, but it may not match the question — please double-check it.');
      } else {
        toast.success('Diagram generated. Save to persist all changes.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Diagram generation failed');
    } finally {
      setGeneratingDiagram(false);
    }
  };

  const handleGenerateAnswerImageClick = async () => {
    if (!question.id) {
      toast.info('Save the question first, then generate the answer diagram.');
      return;
    }
    setGeneratingAnswerImage(true);
    try {
      const res = await api.post(`/admin/question-bank/${question.id}/generate-answer-image`, {
        image_url: imageUrls[0] || null,
      });
      const url = res.data?.data?.correct_solution_image_url || '';
      if (url) {
        setCorrectSolutionImageUrl(url);
        toast.success('Answer diagram generated and saved. Review before saving the question.');
      } else {
        toast.error('No answer image was returned.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate answer diagram.');
    } finally {
      setGeneratingAnswerImage(false);
    }
  };

  const buildPayload = () => {
    const base = {
      type: localType,
      question_text: questionText,
      ...(createMode ? { source_type: 'past-paper' } : {}),
      difficulty,
      default_marks: parseInt(defaultMarks, 10) || 1,
      category_id: categoryId ? Number(categoryId) : null,
      subject_id: subjectId ? Number(subjectId) : null,
      topic_ids: topicIds,
      passage_type: passageType || null,
      explanation: explanation || null,
      image_url: imageUrls[0] || null,
      image_urls: imageUrls.length > 0 ? imageUrls : null,
      image_description: imageDescription.trim() ? imageDescription.trim() : null,
      section_title: sectionTitle.trim() || null,
      section_instructions: sectionInstructions.trim() || null,
      is_sample_question: isSampleQuestion,
      mcq_recommended: mcqRecommended,
      mcq_recommended_note: mcqRecommended ? (mcqRecommendedNote.trim() || null) : null,
    };
    switch (localType) {
      case 'multiple-choice':
        base.options = options;
        base.correct_option_index = correctOptionIndex;
        break;
      case 'multiple-select':
        base.options = options;
        base.correct_option_indices = correctOptionIndices;
        break;
      case 'true-false':
        base.correct_answer = trueFalseAnswer;
        break;
      case 'fill-in-the-blank':
      case 'multi-fill':
        base.correct_blanks = blanks;
        // Always sent, so clearing every row in the editor clears the field on the server.
        base.answer_variants = answerVariants?.length ? answerVariants : null;
        base.answer_rule = answerRule || null;
        base.type = (blanks?.length ?? 0) >= 2 ? 'multi-fill' : 'fill-in-the-blank';
        if (blankLabels.length > 0) base.blank_labels = blankLabels;
        Object.assign(base, fillTypeBlankMarksPayload(blanks.length));
        if (blankLabelPositions.length > 0) base.blank_label_positions = blankLabelPositions;
        if (overlayLayout?.rows_interchangeable) base.overlay_layout = { rows_interchangeable: true, ...(overlayLayout?.cells_interchangeable ? { cells_interchangeable: true } : {}), ...(overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: overlayLayout.interchangeable_row_indices } : {}), ...(interchangeableRowSizeValue(overlayLayout) ? { interchangeable_row_size: interchangeableRowSizeValue(overlayLayout) } : {}) };
        // Combined part: a fill question that also carries a drawing/shade answer (e.g. sketch the
        // rectangles AND type their perimeter/area) — persist it so editing doesn't drop the drawing.
        base.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        if (fillMarksMode === 'fixed') {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          base.default_marks = fixedTotal;
          base.answer_data = {
            marks_mode: 'fixed',
            marks_total: fixedTotal,
            ...(blankLabelPositions.length > 0 ? { blank_label_positions: blankLabelPositions } : {}),
          };
        }
        break;
      case 'table':
        base.correct_blanks = blanks;
        base.answer_variants = answerVariants?.length ? answerVariants : null;
        base.answer_rule = answerRule || null;
        base.type = 'table';
        if (blankLabels.length > 0) base.blank_labels = blankLabels;
        Object.assign(base, fillTypeBlankMarksPayload(blanks.length));
        if (tableLayout?.grid?.length > 0) base.table_layout = tableLayout;
        if (fillMarksMode === 'fixed') {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          base.default_marks = fixedTotal;
          base.answer_data = { marks_mode: 'fixed', marks_total: fixedTotal };
        }
        break;
      case 'image-fill':
        base.correct_blanks = blanks;
        base.answer_variants = answerVariants?.length ? answerVariants : null;
        base.answer_rule = answerRule || null;
        base.type = 'image-fill';
        if (blankLabels.length > 0) base.blank_labels = blankLabels;
        Object.assign(base, fillTypeBlankMarksPayload(blanks.length));
        if (overlayLayout?.image_url || overlayLayout?.mode === 'inline' || overlayLayout?.rows_interchangeable || overlayLayout?.blanks_interchangeable) base.overlay_layout = overlayLayout;
        // Combined part: an image-fill that also carries a drawing/shade answer on a second image.
        base.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        if (fillMarksMode === 'fixed') {
          const fixedTotal = parseInt(fillFixedTotal, 10) || 1;
          base.default_marks = fixedTotal;
          base.answer_data = { marks_mode: 'fixed', marks_total: fixedTotal };
        }
        break;
      case 'matching':
        base.options = options;
        break;
      case 'ordering':
        base.options = options;
        break;
      case 'short-answer':
        base.correct_answer_text = shortAnswer;
        base.answer_rule = answerRule || null;
        break;
      case 'essay':
        base.model_answer = modelAnswer;
        break;
      case 'drawing':
        base.correct_answer_text = shortAnswer;
        base.correct_solution_image_url = correctSolutionImageUrl || null;
        base.solution_geometry = interactiveSpec ? { interactive: interactiveSpec } : null;
        break;
      case 'grouped': {
        const buildSqPayload = (sq) => {
          const p = {
            question_text: sq.question_text,
            type: sq.type,
            default_marks: parseInt(sq.default_marks, 10) || 1,
            explanation: sq.explanation || null,
          };
          if (sq.id) p.id = sq.id;
          p.hide_shared_context = !!sq.hide_shared_context;
          p.mcq_recommended = !!sq.mcq_recommended;
          p.mcq_recommended_note = sq.mcq_recommended ? (String(sq.mcq_recommended_note || '').trim() || null) : null;
          // The sub editor is single-image (it only ever reads/writes sq.image_url), but student
          // rendering prefers image_urls over image_url. Send both so a replaced image can't leave a
          // stale image_urls entry behind to win and paint the old (or since-deleted, 404) figure.
          p.image_url = sq.image_url || null;
          p.image_urls = sq.image_url ? [sq.image_url] : null;
          // Always sent, so emptying the field in the editor clears the field on the server.
          p.image_description = String(sq.image_description || '').trim() || null;
          switch (sq.type) {
            case 'multiple-choice':
              p.options = sq.options;
              p.correct_option_index = sq.correct_option_index;
              break;
            case 'multiple-select':
              p.options = sq.options;
              p.correct_option_indices = sq.correct_option_indices;
              break;
            case 'true-false':
              p.correct_answer = sq.trueFalseAnswer;
              break;
            case 'fill-in-the-blank':
            case 'multi-fill':
              p.correct_blanks = sq.blanks;
              // Always sent, so clearing a row in the editor clears the field on the server.
              p.answer_variants = sq.answerVariants?.length ? sq.answerVariants : null;
              p.answer_rule = sq.answerRule || null;
              p.type = (sq.blanks?.length ?? 0) >= 2 ? 'multi-fill' : 'fill-in-the-blank';
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              Object.assign(p, subFillBlankMarksPayload(sq));
              if (sq.blankLabelPositions?.length > 0) p.blank_label_positions = sq.blankLabelPositions;
              if (sq.overlayLayout?.rows_interchangeable) p.overlay_layout = { rows_interchangeable: true, ...(sq.overlayLayout?.cells_interchangeable ? { cells_interchangeable: true } : {}), ...(sq.overlayLayout?.interchangeable_row_indices?.length ? { interchangeable_row_indices: sq.overlayLayout.interchangeable_row_indices } : {}), ...(interchangeableRowSizeValue(sq.overlayLayout) ? { interchangeable_row_size: interchangeableRowSizeValue(sq.overlayLayout) } : {}) };
              // Combined part: keep the attached drawing/shade spec (e.g. sketch + typed perimeter/area).
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              if (sq.marksMode === 'fixed') {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: 'fixed', marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case 'table':
              p.correct_blanks = sq.blanks;
              p.answer_variants = sq.answerVariants?.length ? sq.answerVariants : null;
              p.answer_rule = sq.answerRule || null;
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              Object.assign(p, subFillBlankMarksPayload(sq));
              if (sq.tableLayout?.grid?.length > 0) p.table_layout = sq.tableLayout;
              if (sq.marksMode === 'fixed') {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: 'fixed', marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case 'image-fill':
              p.correct_blanks = sq.blanks;
              p.answer_variants = sq.answerVariants?.length ? sq.answerVariants : null;
              p.answer_rule = sq.answerRule || null;
              if (sq.blankLabels?.length > 0) p.blank_labels = sq.blankLabels;
              Object.assign(p, subFillBlankMarksPayload(sq));
              if (sq.overlayLayout?.image_url || sq.overlayLayout?.mode === 'inline' || sq.overlayLayout?.rows_interchangeable || sq.overlayLayout?.blanks_interchangeable) p.overlay_layout = sq.overlayLayout;
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              if (sq.marksMode === 'fixed') {
                const ft = parseInt(sq.fixedTotal, 10) || 1;
                p.answer_data = { ...(p.answer_data || {}), marks_mode: 'fixed', marks_total: ft };
                p.default_marks = ft;
              }
              break;
            case 'short-answer':
              p.correct_answer_text = sq.shortAnswer;
              p.answer_rule = sq.answerRule || null;
              break;
            case 'essay':
              p.model_answer = sq.modelAnswer;
              break;
            case 'drawing':
              p.correct_answer_text = sq.shortAnswer;
              p.correct_solution_image_url = sq.correctSolutionImageUrl || null;
              p.solution_geometry = sq.interactiveSpec ? { interactive: sq.interactiveSpec } : null;
              break;
            case 'matching':
              p.options = sq.options;
              break;
            case 'ordering':
              p.options = sq.options;
              break;
            default:
              break;
          }
          // Typed sub-answers use the same fuzzy tiers as a standalone question.
          if (['fill-in-the-blank', 'multi-fill', 'short-answer', 'table', 'image-fill'].includes(sq.type)) {
            p.exact_match = !!sq.exactMatch;
            p.semantic_match = !sq.exactMatch && !!sq.semanticMatch;
          }
          if (sq.sub_questions?.length > 0) {
            p.sub_questions = sq.sub_questions.map(buildSqPayload);
          }
          return p;
        };
        base.sub_label_type = subLabelType;
        if (groupedMarksMode === 'fixed') {
          const fixedTotal = parseInt(groupedFixedTotal, 10) || 1;
          base.default_marks = fixedTotal;
          base.answer_data = { marks_mode: 'fixed', marks_total: fixedTotal };
          base.sub_questions = subQuestions.map((sq) => ({ ...buildSqPayload(sq), default_marks: 1 }));
        } else {
          base.sub_questions = subQuestions.map(buildSqPayload);
          base.default_marks = Math.max(1, sumSubQuestionMarks(subQuestions));
        }
        base.answer_data = { ...(base.answer_data || {}), extend_parts: groupedExtend };
        break;
      }
      default:
        break;
    }
    base.force_measurement_tool = forceMeasurementTool === 'auto' ? null : forceMeasurementTool;
    // Typed answers of every shape (one box, many blanks, a table, an image overlay) run through the same
    // fuzzy tiers, so "must be written exactly like this" applies to all of them, not just short-answer.
    if (['fill-in-the-blank', 'multi-fill', 'short-answer', 'table', 'image-fill'].includes(localType)) {
      base.exact_match = !!exactMatch;
      base.semantic_match = !exactMatch && !!semanticMatch;
    }

    return base;
  };

  // Every typed answer type gets this: the fuzzy tiers (and the AI grader) apply to blanks, tables and
  // image-fill boxes exactly as they do to a single short-answer box.
  const matchModeTicks = (
    <>
    <FormControlLabel
      sx={{ mb: 1, display: 'block' }}
      control={<Checkbox checked={!!semanticMatch} onChange={(e) => setSemanticMatch(e.target.checked)} size="small" disabled={!!exactMatch} />}
      label={
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          Grade with AI (accept answers phrased differently) — for prose answers such as “explain how you
          know” or “write two other things that are the same about all four shapes”
        </Typography>
      }
    />
    <FormControlLabel
      sx={{ mb: 1, display: 'block' }}
      control={<Checkbox checked={!!exactMatch} onChange={(e) => { setExactMatch(e.target.checked); if (e.target.checked) setSemanticMatch(false); }} size="small" />}
      label={
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
          Answer must be written exactly like this (no near-enough matching, no AI) — e.g. an expression
          such as “2(3x + 5) + 2(7x + 2)”, where a missing “+” is wrong
        </Typography>
      }
    />
    </>
  );

  const renderAnswerEditor = () => {
    switch (localType) {
      case 'multiple-choice':
        return (
          <McqOptions
            options={options}
            setOptions={setOptions}
            correctIndex={correctOptionIndex}
            setCorrectIndex={setCorrectOptionIndex}
          />
        );
      case 'multiple-select':
        return (
          <McqOptions
            options={options}
            setOptions={setOptions}
            correctIndices={correctOptionIndices}
            setCorrectIndices={setCorrectOptionIndices}
            isMultiSelect
          />
        );
      case 'true-false':
        return <TrueFalseOptions value={trueFalseAnswer} onChange={setTrueFalseAnswer} />;
      case 'fill-in-the-blank':
        return (
          <>
            {matchModeTicks}
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              cellsInterchangeable={overlayLayout?.cells_interchangeable}
              onCellsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, cells_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              interchangeableRowSize={overlayLayout?.interchangeable_row_size ?? ""}
              onInterchangeableRowSizeChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_size: v })}
            />
            <FillInBlankEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabelPositions={blankLabelPositions}
              setBlankLabelPositions={setBlankLabelPositions}
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <AnswerVariantsEditor
              variants={answerVariants}
              setVariants={setAnswerVariants}
              blankCount={blanks.length}
              blankLabels={blankLabels}
            />
            <AnswerRuleEditor rule={answerRule} setRule={setAnswerRule} blankCount={blanks.length} />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case 'multi-fill':
        return (
          <>
            {matchModeTicks}
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              cellsInterchangeable={overlayLayout?.cells_interchangeable}
              onCellsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, cells_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              interchangeableRowSize={overlayLayout?.interchangeable_row_size ?? ""}
              onInterchangeableRowSizeChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_size: v })}
            />
            <FillInBlankEditor
              blanks={blanks}
              setBlanks={setBlanks}
              isMulti
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabelPositions={blankLabelPositions}
              setBlankLabelPositions={setBlankLabelPositions}
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <AnswerVariantsEditor
              variants={answerVariants}
              setVariants={setAnswerVariants}
              blankCount={blanks.length}
              blankLabels={blankLabels}
            />
            <AnswerRuleEditor rule={answerRule} setRule={setAnswerRule} blankCount={blanks.length} />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case 'table':
        return (
          <>
            {matchModeTicks}
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
            />
            <TableFillEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              tableLayout={tableLayout}
              setTableLayout={setTableLayout}
              showMathTools
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <AnswerVariantsEditor
              variants={answerVariants}
              setVariants={setAnswerVariants}
              blankCount={blanks.length}
              blankLabels={blankLabels}
            />
            <AnswerRuleEditor rule={answerRule} setRule={setAnswerRule} blankCount={blanks.length} />
          </>
        );
      case 'image-fill':
        return (
          <>
            {matchModeTicks}
            <FillTypeMarksControls
              marksMode={fillMarksMode}
              onMarksModeChange={setFillMarksMode}
              fixedTotal={fillFixedTotal}
              onFixedTotalChange={setFillFixedTotal}
              blankCount={blanks.length}
              showRowsInterchangeable
              rowsInterchangeable={overlayLayout?.rows_interchangeable}
              onRowsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, rows_interchangeable: v })}
              cellsInterchangeable={overlayLayout?.cells_interchangeable}
              onCellsInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, cells_interchangeable: v })}
              interchangeableRowIndices={overlayLayout?.interchangeable_row_indices || []}
              onInterchangeableRowIndicesChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_indices: v })}
              interchangeableRowSize={overlayLayout?.interchangeable_row_size ?? ""}
              onInterchangeableRowSizeChange={(v) => setOverlayLayout({ ...overlayLayout, interchangeable_row_size: v })}
              blanksInterchangeable={overlayLayout?.blanks_interchangeable}
              onBlanksInterchangeableChange={(v) => setOverlayLayout({ ...overlayLayout, blanks_interchangeable: v })}
            />
            <ImageOverlayFillEditor
              blanks={blanks}
              setBlanks={setBlanks}
              blankMarks={blankMarks}
              setBlankMarks={setBlankMarks}
              blankLabels={blankLabels}
              setBlankLabels={setBlankLabels}
              overlayLayout={overlayLayout}
              setOverlayLayout={setOverlayLayout}
              attachedImages={imageUrls}
              fillMarksMode={fillMarksMode}
              fillFixedTotal={fillFixedTotal}
            />
            <AnswerVariantsEditor
              variants={answerVariants}
              setVariants={setAnswerVariants}
              blankCount={blanks.length}
              blankLabels={blankLabels}
            />
            <AnswerRuleEditor rule={answerRule} setRule={setAnswerRule} blankCount={blanks.length} />
            <CombinedDrawingAnswer
              interactiveSpec={interactiveSpec}
              setInteractiveSpec={setInteractiveSpec}
              attachedImages={imageUrls}
            />
          </>
        );
      case 'short-answer':
        return (
          <>
            {matchModeTicks}
            <ShortAnswerEditor value={shortAnswer} onChange={setShortAnswer} showMathTools />
            <AnswerRuleEditor rule={answerRule} setRule={setAnswerRule} blankCount={1} />
          </>
        );
      case 'essay':
        return <EssayAnswer value={modelAnswer} onChange={setModelAnswer} showMathTools />;
      case 'drawing':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl size="small" sx={{ maxWidth: 320 }}>
              <InputLabel>Answer mode</InputLabel>
              <Select
                label="Answer mode"
                value={drawingAnswerMode}
                // Re-selecting the SAME mode must not touch the spec. This used to replace it wholesale with
                // { kind }, which silently destroyed everything the mode had already been given — targets,
                // match_orientation, pos_tol, symbol_palette. Re-saving a finished question through the
                // dialog therefore wiped its answer: "Reflect the word SOW" came back with
                // match_orientation false, so an UNFLIPPED letter graded correct.
                // Genuinely changing the mode still resets (the old kind's fields are meaningless to the new
                // one), but the image is carried across so the author doesn't have to re-pick it.
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === drawingAnswerMode) return;
                  if (next === 'free') {
                    setInteractiveSpec(null);
                    return;
                  }
                  setInteractiveSpec({
                    kind: next,
                    ...(interactiveSpec?.image_url ? { image_url: interactiveSpec.image_url } : {}),
                  });
                }}
              >
                <MenuItem value="free">Free draw (AI vision)</MenuItem>
                <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                <MenuItem value="segments">Join points (segments)</MenuItem>
                <MenuItem value="pictogram">Pictogram (fill icons)</MenuItem>
                <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                <MenuItem value="cut-drag">Cut shape &amp; drag pieces (rearrange)</MenuItem>
                <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                <MenuItem value="marker">Arrow / bar marker (value, or bars, on an axis)</MenuItem>
              </Select>
            </FormControl>
            {drawingAnswerMode === 'free' ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Free draw: the student draws on the pad; grading compares against a generated answer
                  diagram using AI vision. Interactive modes above grade instantly with no AI.
                </Typography>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.5 }}>Answer description (optional)</Typography>
                  <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 90, borderRadius: '0 0 6px 6px' } }}>
                    <QuillR2Editor
                      value={shortAnswer}
                      onChange={(html) => setShortAnswer(normalizeQuillValue(html))}
                      modules={EXPL_QUILL_MODULES}
                      formats={EXPL_QUILL_FORMATS}
                      placeholder="e.g. Reflect the triangle across the line; vertices mirror onto the opposite side. Add an image of the worked solution if helpful."
                      style={{ minHeight: 110 }}
                      showMathTools
                    />
                  </Box>
                </Box>
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={generatingAnswerImage ? <CircularProgress size={14} /> : <AutoAwesome />}
                    onClick={handleGenerateAnswerImageClick}
                    disabled={generatingAnswerImage || createMode || !question.id}
                    sx={{ borderColor: '#0ea5e9', color: '#0ea5e9', textTransform: 'none' }}
                  >
                    {generatingAnswerImage ? 'Generating…' : 'Generate Answer Diagram'}
                  </Button>
                </Box>
                {correctSolutionImageUrl ? (
                  <Box sx={{ display: 'inline-flex', position: 'relative', alignSelf: 'flex-start' }}>
                    <img
                      src={correctSolutionImageUrl}
                      alt="Correct answer diagram"
                      style={{ maxWidth: 320, maxHeight: 260, borderRadius: 4, border: '1px solid #e0e0e0', objectFit: 'contain' }}
                      onError={(e) => { e.target.style.opacity = '0.3'; }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setCorrectSolutionImageUrl('')}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', color: '#f44336', p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    No answer diagram yet — attach the question image, save, then click Generate.
                  </Typography>
                )}
              </>
            ) : (
              <>
                {imageUrls.length > 1 ? (
                  <FormControl size="small" sx={{ maxWidth: 360 }}>
                    <InputLabel>Draw on which image</InputLabel>
                    <Select
                      label="Draw on which image"
                      value={interactiveSpec?.image_url || imageUrls[0] || ''}
                      onChange={(e) => setInteractiveSpec({ ...interactiveSpec, image_url: e.target.value })}
                      renderValue={(v) => {
                        const i = imageUrls.indexOf(v);
                        return i >= 0 ? `Image ${i + 1}` : 'Image 1';
                      }}
                    >
                      {imageUrls.map((u, i) => (
                        <MenuItem key={u} value={u}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box
                              component="img"
                              src={u}
                              alt=""
                              sx={{ width: 48, height: 36, objectFit: 'contain', borderRadius: 0.5, border: '1px solid #e0e0e0', bgcolor: '#fff' }}
                              onError={(e) => { e.target.style.opacity = '0.3'; }}
                            />
                            <Typography variant="body2">Image {i + 1}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                <InteractiveDiagramEditor
                  kind={drawingAnswerMode}
                  spec={interactiveSpec}
                  onChange={setInteractiveSpec}
                  imageUrl={interactiveSpec?.image_url || imageUrls[0] || ''}
                />
              </>
            )}
          </Box>
        );
      case 'matching':
        return <MatchingEditor options={options} setOptions={setOptions} />;
      case 'ordering':
        return <OrderingEditor options={options} setOptions={setOptions} />;
      case 'grouped':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Sub-question labels</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={subLabelType}
                  exclusive
                  size="small"
                  onChange={(_, val) => { if (val) setSubLabelType(val); }}
                  sx={{ height: 28 }}
                >
                  <ToggleButton value="letter" sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>a, b, c</ToggleButton>
                  <ToggleButton value="roman" sx={{ px: 1.5, fontSize: '0.72rem', textTransform: 'none' }}>i, ii, iii</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={handleAddSubQuestion}
                  sx={{ borderColor: '#6366f1', color: '#6366f1', textTransform: 'none', py: 0.25 }}
                >
                  Add Sub-question
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupedMarksMode === 'fixed'}
                    onChange={(e) => setGroupedMarksMode(e.target.checked ? 'fixed' : 'sum')}
                    size="small"
                  />
                }
                label={<Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>Fixed total marks (fractional)</Typography>}
                sx={{ m: 0 }}
              />
              {groupedMarksMode === 'fixed' && (
                <TextField
                  size="small"
                  label="Total marks"
                  type="number"
                  value={groupedFixedTotal}
                  onChange={(e) => setGroupedFixedTotal(e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ width: 130 }}
                  helperText={subQuestions.length > 0 ? `${groupedFixedTotal}/${subQuestions.length} per part` : ''}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupedExtend}
                    onChange={(e) => setGroupedExtend(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>Show all parts extended (one question) — for plotting/drawing on a shared grid</Typography>}
                sx={{ m: 0 }}
              />
              <UnifySharedGridsControl
                subQuestions={subQuestions}
                setSubQuestions={setSubQuestions}
                labelOf={(i) => (subLabelType === 'roman' ? toRoman(i + 1) : String.fromCharCode(97 + i))}
              />
            </Box>

            <SharedGridPartsEditor
              subQuestions={subQuestions}
              setSubQuestions={setSubQuestions}
              labelOf={(i) => (subLabelType === 'roman' ? toRoman(i + 1) : String.fromCharCode(97 + i))}
            />
            {subQuestions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No sub-questions yet. Click "Add Sub-question" to start.</Typography>
            )}
            {subQuestions.map((sq, idx) => {
              const label = subLabelType === 'roman' ? toRoman(idx + 1) : String.fromCharCode(97 + idx);
              const isOpen = expandedSq === idx;
              const updateSq = (updates) =>
                setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...updates } : s)));

              const renderSqAnswer = () => {
                switch (sq.type) {
                  case 'multiple-choice':
                    return <McqOptions options={sq.options} setOptions={(o) => updateSq({ options: o })} correctIndex={sq.correct_option_index} setCorrectIndex={(v) => updateSq({ correct_option_index: v })} />;
                  case 'multiple-select':
                    return <McqOptions options={sq.options} setOptions={(o) => updateSq({ options: o })} correctIndices={sq.correct_option_indices} setCorrectIndices={(v) => updateSq({ correct_option_indices: v })} isMultiSelect />;
                  case 'true-false':
                    return <TrueFalseOptions value={sq.trueFalseAnswer} onChange={(v) => updateSq({ trueFalseAnswer: v })} />;
                  case 'fill-in-the-blank':
                  case 'multi-fill':
                    return (
                      <>
                        <FillTypeMarksControls
                          marksMode={sq.marksMode}
                          onMarksModeChange={(mode) => updateSq({ marksMode: mode })}
                          fixedTotal={sq.fixedTotal}
                          onFixedTotalChange={(v) => updateSq({ fixedTotal: v })}
                          blankCount={sq.blanks?.length || 0}
                          showSemanticMatch
                          semanticMatch={sq.semanticMatch}
                          onSemanticMatchChange={(v) => updateSq({ semanticMatch: v })}
                          exactMatch={sq.exactMatch}
                          onExactMatchChange={(v) => updateSq({ exactMatch: v })}
                          showRowsInterchangeable
                          rowsInterchangeable={sq.overlayLayout?.rows_interchangeable}
                          onRowsInterchangeableChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, rows_interchangeable: v } })}
                          cellsInterchangeable={sq.overlayLayout?.cells_interchangeable}
                          onCellsInterchangeableChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, cells_interchangeable: v } })}
                          interchangeableRowIndices={sq.overlayLayout?.interchangeable_row_indices || []}
                          onInterchangeableRowIndicesChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_indices: v } })}
                          interchangeableRowSize={sq.overlayLayout?.interchangeable_row_size ?? ""}
                          onInterchangeableRowSizeChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_size: v } })}
                        />
                        <FillInBlankEditor blanks={sq.blanks} setBlanks={(b) => updateSq({ blanks: b })} isMulti={sq.type === 'multi-fill'} blankLabels={sq.blankLabels} setBlankLabels={(l) => updateSq({ blankLabels: l })} blankMarks={sq.blankMarks} setBlankMarks={(m) => updateSq({ blankMarks: m })} blankLabelPositions={sq.blankLabelPositions} setBlankLabelPositions={(p) => updateSq({ blankLabelPositions: p })} fillMarksMode={sq.marksMode} fillFixedTotal={sq.fixedTotal} />
                        <AnswerVariantsEditor
                          variants={sq.answerVariants}
                          setVariants={(v) => updateSq({ answerVariants: v })}
                          blankCount={sq.blanks?.length || 0}
                          blankLabels={sq.blankLabels || []}
                        />
                        <AnswerRuleEditor
                          rule={sq.answerRule}
                          setRule={(r) => updateSq({ answerRule: r })}
                          blankCount={sq.blanks?.length || 0}
                        />
                        <CombinedDrawingAnswer
                          interactiveSpec={sq.interactiveSpec}
                          setInteractiveSpec={(s) => updateSq({ interactiveSpec: s })}
                          attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...imageUrls].filter((u, i, a) => u && a.indexOf(u) === i)}
                        />
                      </>
                    );
                  case 'table':
                    return (
                      <>
                        <FillTypeMarksControls
                          marksMode={sq.marksMode}
                          onMarksModeChange={(mode) => updateSq({ marksMode: mode })}
                          fixedTotal={sq.fixedTotal}
                          onFixedTotalChange={(v) => updateSq({ fixedTotal: v })}
                          blankCount={sq.blanks?.length || 0}
                          showSemanticMatch
                          semanticMatch={sq.semanticMatch}
                          onSemanticMatchChange={(v) => updateSq({ semanticMatch: v })}
                          exactMatch={sq.exactMatch}
                          onExactMatchChange={(v) => updateSq({ exactMatch: v })}
                        />
                        <TableFillEditor blanks={sq.blanks} setBlanks={(b) => updateSq({ blanks: b })} blankMarks={sq.blankMarks} setBlankMarks={(m) => updateSq({ blankMarks: m })} blankLabels={sq.blankLabels} setBlankLabels={(l) => updateSq({ blankLabels: l })} tableLayout={sq.tableLayout} setTableLayout={(t) => updateSq({ tableLayout: t })} showMathTools fillMarksMode={sq.marksMode} fillFixedTotal={sq.fixedTotal} />
                        <AnswerVariantsEditor
                          variants={sq.answerVariants}
                          setVariants={(v) => updateSq({ answerVariants: v })}
                          blankCount={sq.blanks?.length || 0}
                          blankLabels={sq.blankLabels || []}
                        />
                        <AnswerRuleEditor
                          rule={sq.answerRule}
                          setRule={(r) => updateSq({ answerRule: r })}
                          blankCount={sq.blanks?.length || 0}
                        />
                      </>
                    );
                  case 'image-fill':
                    return (
                      <>
                        <FillTypeMarksControls
                          marksMode={sq.marksMode}
                          onMarksModeChange={(mode) => updateSq({ marksMode: mode })}
                          fixedTotal={sq.fixedTotal}
                          onFixedTotalChange={(v) => updateSq({ fixedTotal: v })}
                          blankCount={sq.blanks?.length || 0}
                          showSemanticMatch
                          semanticMatch={sq.semanticMatch}
                          onSemanticMatchChange={(v) => updateSq({ semanticMatch: v })}
                          exactMatch={sq.exactMatch}
                          onExactMatchChange={(v) => updateSq({ exactMatch: v })}
                          showRowsInterchangeable
                          rowsInterchangeable={sq.overlayLayout?.rows_interchangeable}
                          onRowsInterchangeableChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, rows_interchangeable: v } })}
                          cellsInterchangeable={sq.overlayLayout?.cells_interchangeable}
                          onCellsInterchangeableChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, cells_interchangeable: v } })}
                          interchangeableRowIndices={sq.overlayLayout?.interchangeable_row_indices || []}
                          onInterchangeableRowIndicesChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_indices: v } })}
                          interchangeableRowSize={sq.overlayLayout?.interchangeable_row_size ?? ""}
                          onInterchangeableRowSizeChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, interchangeable_row_size: v } })}
                          blanksInterchangeable={sq.overlayLayout?.blanks_interchangeable}
                          onBlanksInterchangeableChange={(v) => updateSq({ overlayLayout: { ...sq.overlayLayout, blanks_interchangeable: v } })}
                        />
                        <ImageOverlayFillEditor blanks={sq.blanks} setBlanks={(b) => updateSq({ blanks: b })} blankMarks={sq.blankMarks} setBlankMarks={(m) => updateSq({ blankMarks: m })} blankLabels={sq.blankLabels} setBlankLabels={(l) => updateSq({ blankLabels: l })} overlayLayout={sq.overlayLayout} setOverlayLayout={(o) => updateSq({ overlayLayout: o })} attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...imageUrls].filter((u, i, a) => u && a.indexOf(u) === i)} fillMarksMode={sq.marksMode} fillFixedTotal={sq.fixedTotal} />
                        <AnswerVariantsEditor
                          variants={sq.answerVariants}
                          setVariants={(v) => updateSq({ answerVariants: v })}
                          blankCount={sq.blanks?.length || 0}
                          blankLabels={sq.blankLabels || []}
                        />
                        <AnswerRuleEditor
                          rule={sq.answerRule}
                          setRule={(r) => updateSq({ answerRule: r })}
                          blankCount={sq.blanks?.length || 0}
                        />
                        <CombinedDrawingAnswer
                          interactiveSpec={sq.interactiveSpec}
                          setInteractiveSpec={(s) => updateSq({ interactiveSpec: s })}
                          attachedImages={[...(sq.image_url ? [sq.image_url] : []), ...imageUrls].filter((u, i, a) => u && a.indexOf(u) === i)}
                        />
                      </>
                    );
                  case 'short-answer':
                    return (
                      <>
                        {/* An "explain how you know" part is prose no fuzzy tier can match — it needs the
                            AI grader a standalone short answer already offers. */}
                        <FormControlLabel
                          sx={{ mb: 1, display: 'block' }}
                          control={<Checkbox checked={!!sq.semanticMatch} onChange={(e) => updateSq({ semanticMatch: e.target.checked })} size="small" disabled={!!sq.exactMatch} />}
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                              Grade with AI (accept answers phrased differently)
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          sx={{ mb: 1, display: 'block' }}
                          control={<Checkbox checked={!!sq.exactMatch} onChange={(e) => updateSq({ exactMatch: e.target.checked, ...(e.target.checked ? { semanticMatch: false } : {}) })} size="small" />}
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                              Answer must be written exactly like this (no near-enough matching, no AI)
                            </Typography>
                          }
                        />
                        <ShortAnswerEditor value={sq.shortAnswer} onChange={(v) => updateSq({ shortAnswer: v })} showMathTools />
                        <AnswerRuleEditor
                          rule={sq.answerRule}
                          setRule={(r) => updateSq({ answerRule: r })}
                          blankCount={1}
                        />
                      </>
                    );
                  case 'essay':
                    return <EssayAnswer value={sq.modelAnswer} onChange={(v) => updateSq({ modelAnswer: v })} showMathTools />;
                  case 'drawing': {
                    const sqDrawMode = sq.interactiveSpec?.kind || 'free';
                    const sqDrawImages = [...(sq.image_url ? [sq.image_url] : []), ...imageUrls].filter((u, i, a) => u && a.indexOf(u) === i);
                    const sqDrawImage = sq.interactiveSpec?.image_url || sqDrawImages[0] || '';
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormControl size="small" sx={{ maxWidth: 320 }}>
                          <InputLabel>Answer mode</InputLabel>
                          <Select
                            label="Answer mode"
                            value={sqDrawMode}
                            // Same trap as the top-level Answer mode: re-selecting the SAME mode used to
                            // replace the spec with { kind }, wiping the sub-question's targets and settings.
                            onChange={(e) => {
                              const next = e.target.value;
                              if (next === sqDrawMode) return;
                              if (next === 'free') {
                                updateSq({ interactiveSpec: null });
                                return;
                              }
                              updateSq({
                                interactiveSpec: {
                                  kind: next,
                                  ...(sq.interactiveSpec?.image_url ? { image_url: sq.interactiveSpec.image_url } : {}),
                                },
                              });
                            }}
                          >
                            <MenuItem value="free">Free draw (AI vision)</MenuItem>
                            <MenuItem value="shade">Shade regions (tap to fill)</MenuItem>
                            <MenuItem value="points">Place points / reflect (tap grid)</MenuItem>
                            <MenuItem value="rect">Draw rectangle (area match)</MenuItem>
                            <MenuItem value="lines">Draw lines (symmetry / mirror)</MenuItem>
                            <MenuItem value="polygon">Draw shape by area (triangle / square)</MenuItem>
                            <MenuItem value="glyph">Place / reflect / rotate letters</MenuItem>
                            <MenuItem value="segments">Join points (segments)</MenuItem>
                            <MenuItem value="pictogram">Pictogram (fill icons)</MenuItem>
                            <MenuItem value="drag-shape">Drag-copy shape (translation)</MenuItem>
                            <MenuItem value="cut-drag">Cut shape &amp; drag pieces (rearrange)</MenuItem>
                            <MenuItem value="reflect">Reflect shape (auto-computed)</MenuItem>
                            <MenuItem value="rotate">Rotate shape (auto-computed)</MenuItem>
                            <MenuItem value="clock">Clock hands (set the time)</MenuItem>
                            <MenuItem value="marker">Arrow / bar marker (value, or bars, on an axis)</MenuItem>
                          </Select>
                        </FormControl>
                        {sqDrawMode === 'free' ? (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              Free draw: graded against a generated answer diagram via AI vision. Interactive
                              modes grade instantly with no AI.
                            </Typography>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              minRows={2}
                              label="Answer description (optional)"
                              value={sq.shortAnswer || ''}
                              onChange={(e) => updateSq({ shortAnswer: e.target.value })}
                            />
                            <Box>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={subSqLoading?.idx === idx && subSqLoading?.kind === 'answer-image' ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: '0.9rem !important' }} />}
                                onClick={() => handleSubSqGenerateAnswerImage(idx, sq)}
                                disabled={!!subSqLoading || !sq.id}
                                sx={{ textTransform: 'none' }}
                              >
                                {subSqLoading?.idx === idx && subSqLoading?.kind === 'answer-image' ? 'Generating…' : 'Generate Answer Diagram'}
                              </Button>
                            </Box>
                            {sq.correctSolutionImageUrl ? (
                              <Box sx={{ display: 'inline-flex', position: 'relative', alignSelf: 'flex-start' }}>
                                <img
                                  src={sq.correctSolutionImageUrl}
                                  alt="Correct answer diagram"
                                  style={{ maxWidth: 280, maxHeight: 220, borderRadius: 4, border: '1px solid #e0e0e0', objectFit: 'contain' }}
                                  onError={(e) => { e.target.style.opacity = '0.3'; }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => updateSq({ correctSolutionImageUrl: '' })}
                                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', color: '#f44336', p: 0.25 }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                Attach the sub-question image and save, then click Generate.
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            {sqDrawImages.length > 1 ? (
                              <FormControl size="small" sx={{ maxWidth: 360 }}>
                                <InputLabel>Draw on which image</InputLabel>
                                <Select
                                  label="Draw on which image"
                                  value={sqDrawImage}
                                  onChange={(e) => updateSq({ interactiveSpec: { ...sq.interactiveSpec, image_url: e.target.value } })}
                                  renderValue={(v) => {
                                    const i = sqDrawImages.indexOf(v);
                                    return i >= 0 ? `Image ${i + 1}` : 'Image 1';
                                  }}
                                >
                                  {sqDrawImages.map((u, i) => (
                                    <MenuItem key={u} value={u}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <Box
                                          component="img"
                                          src={u}
                                          alt=""
                                          sx={{ width: 48, height: 36, objectFit: 'contain', borderRadius: 0.5, border: '1px solid #e0e0e0', bgcolor: '#fff' }}
                                          onError={(e) => { e.target.style.opacity = '0.3'; }}
                                        />
                                        <Typography variant="body2">Image {i + 1}</Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : null}
                            <InteractiveDiagramEditor
                              kind={sqDrawMode}
                              spec={sq.interactiveSpec}
                              onChange={(spec) => updateSq({ interactiveSpec: spec })}
                              imageUrl={sqDrawImage}
                            />
                          </>
                        )}
                      </Box>
                    );
                  }
                  case 'matching':
                    return <MatchingEditor options={sq.options} setOptions={(o) => updateSq({ options: o })} />;
                  case 'ordering':
                    return <OrderingEditor options={sq.options} setOptions={(o) => updateSq({ options: o })} />;
                  default:
                    return null;
                }
              };

              return (
                <Box key={sq.id ?? idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                  <Box
                    onClick={() => setExpandedSq(isOpen ? null : idx)}
                    sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', bgcolor: isOpen ? 'rgba(99,102,241,0.05)' : '#fafafa', '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' } }}
                  >
                    <Chip label={`(${label})`} size="small" sx={{ fontWeight: 700, bgcolor: '#3f2b96', color: '#fff', fontSize: '0.75rem', height: 22 }} />
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {stripToPlainText(sq.question_text || '').trim() || `Sub-question ${idx + 1}`}
                    </Typography>
                    <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 150 }}>
                      <QuestionTypeSelector
                        value={sq.type || 'short-answer'}
                        onChange={(e) => { e.stopPropagation(); handleSqTypeChange(idx, e.target.value); }}
                      />
                    </Box>
                    {groupedMarksMode === 'fixed' ? (
                      <Chip
                        label={`${parseInt(groupedFixedTotal, 10) || 1}/${subQuestions.length}`}
                        size="small" color="primary" variant="outlined"
                        sx={{ fontWeight: 700, minWidth: 56 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={sq.default_marks}
                        onChange={(e) => { e.stopPropagation(); updateSq({ default_marks: e.target.value }); }}
                        onClick={(e) => e.stopPropagation()}
                        label="Marks"
                        inputProps={{ min: 1 }}
                        sx={{ width: 72 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDeleteSubQuestion(idx); }}
                      title="Remove sub-question"
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    {isOpen ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
                  </Box>
                  <Collapse in={isOpen}>
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, borderTop: '1px solid #f0f0f0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>Question text</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={subSqLoading?.idx === idx && subSqLoading?.kind === 'improve' ? <CircularProgress size={12} /> : <AutoFixHigh sx={{ fontSize: '0.9rem !important' }} />}
                          onClick={() => handleSubSqAiImprove(idx, sq)}
                          disabled={!!subSqLoading}
                          sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          {subSqLoading?.idx === idx && subSqLoading?.kind === 'improve' ? 'Working…' : 'AI Improve'}
                        </Button>
                      </Box>
                      <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 80, borderRadius: '0 0 6px 6px', fontSize: '0.95rem' } }}>
                        <QuillR2Editor
                          value={sq.question_text || ''}
                          onChange={(html) => updateSq({ question_text: html })}
                          modules={QUILL_MODULES}
                          formats={QUILL_FORMATS}
                          placeholder="Sub-question text…"
                          showMathTools
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 0.5 }}>
                          Image (optional)
                        </Typography>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          style={{ display: 'none' }}
                          ref={(el) => { subSqFileInputRefs.current[idx] = el; }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleSubSqImageUpload(idx, file);
                          }}
                          disabled={subSqImageUploading !== null}
                        />
                        {sq.image_url ? (
                          <Box sx={{ display: 'inline-flex', position: 'relative', mb: 1 }}>
                            <img
                              src={sq.image_url}
                              alt="sub-question"
                              style={{ maxWidth: 160, maxHeight: 120, borderRadius: 4, border: '1px solid #e0e0e0', objectFit: 'contain' }}
                              onError={(e) => { e.target.style.opacity = '0.3'; }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleSubSqImageRemove(idx)}
                              sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', color: '#f44336', p: 0.25 }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        ) : null}
                        <Box
                          onDragOver={(e) => {
                            if (e.dataTransfer?.types?.includes('Files')) {
                              e.preventDefault();
                              setSubSqDragOver(idx);
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) setSubSqDragOver(null);
                          }}
                          onDrop={async (e) => {
                            const file = e.dataTransfer?.files?.[0];
                            if (!file) return;
                            e.preventDefault();
                            setSubSqDragOver(null);
                            await handleSubSqImageUpload(idx, file);
                          }}
                          onClick={() => subSqImageUploading === null && triggerSubSqImageUpload(idx)}
                          sx={{
                            border: '1px dashed',
                            borderColor: subSqDragOver === idx ? '#6366f1' : 'divider',
                            bgcolor: subSqDragOver === idx ? 'rgba(99,102,241,0.06)' : 'grey.50',
                            borderRadius: 1.5,
                            px: 2,
                            py: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            cursor: subSqImageUploading !== null ? 'not-allowed' : 'pointer',
                            '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.03)' },
                          }}
                        >
                          {subSqImageUploading === idx ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudUpload sx={{ fontSize: 16, color: 'text.disabled' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem' }}>
                                {sq.image_url ? 'Drop to replace, or click to browse' : 'Drop image here, or click to browse'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          label="Image description (optional)"
                          value={sq.image_description || ''}
                          onChange={(e) => updateSq({ image_description: e.target.value })}
                          placeholder="Accessibility / diagram generation hint"
                          sx={{ mt: 1 }}
                          InputProps={{
                            endAdornment: sq.image_description ? (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => updateSq({ image_description: '' })} title="Delete description">
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      </Box>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            checked={!!sq.hide_shared_context}
                            onChange={(e) => updateSq({ hide_shared_context: e.target.checked })}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                            Hide shared passage/images for this part (standalone question)
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            checked={!!sq.mcq_recommended}
                            onChange={(e) => updateSq({ mcq_recommended: e.target.checked })}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                            Recommend converting this part to MCQ (hard to answer online)
                          </Typography>
                        }
                      />
                      {!!sq.mcq_recommended && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Note shown to students (optional)"
                          value={sq.mcq_recommended_note || ''}
                          onChange={(e) => updateSq({ mcq_recommended_note: e.target.value })}
                          inputProps={{ maxLength: 500 }}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>Answer Configuration</Typography>
                          <TextField
                            size="small"
                            type="number"
                            label="Marks"
                            value={sq.default_marks}
                            onChange={(e) => updateSq({ default_marks: e.target.value })}
                            inputProps={{ min: 1 }}
                            sx={{ width: 88 }}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            subSqLoading?.idx === idx && subSqLoading?.kind === 'answer' ? (
                              <CircularProgress size={12} />
                            ) : (
                              <AutoAwesome sx={{ fontSize: '0.9rem !important' }} />
                            )
                          }
                          onClick={() => handleSubSqGenerateAnswer(idx, sq)}
                          disabled={!!subSqLoading || !sq.id}
                          title={!sq.id ? 'Save the question first so this sub-question has an ID' : ''}
                          sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          {subSqLoading?.idx === idx && subSqLoading?.kind === 'answer' ? 'Generating…' : 'AI Generate Answer'}
                        </Button>
                      </Box>
                      {renderSqAnswer()}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>Explanation (Optional)</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            subSqLoading?.idx === idx && subSqLoading?.kind === 'expl' ? (
                              <CircularProgress size={12} />
                            ) : (
                              <AutoAwesome sx={{ fontSize: '0.9rem !important' }} />
                            )
                          }
                          onClick={() => handleSubSqGenerateExplanation(idx, sq)}
                          disabled={!!subSqLoading}
                          sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                        >
                          {subSqLoading?.idx === idx && subSqLoading?.kind === 'expl' ? 'Generating…' : 'AI Generate'}
                        </Button>
                      </Box>
                      <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 100, borderRadius: '0 0 6px 6px', fontSize: '0.9rem' } }}>
                        <QuillR2Editor
                          value={sq.explanation || ''}
                          onChange={(html) => updateSq({ explanation: normalizeQuillValue(html) })}
                          modules={EXPL_QUILL_MODULES}
                          formats={EXPL_QUILL_FORMATS}
                          placeholder="Explain the correct answer…"
                          style={{ minHeight: 120 }}
                          showMathTools
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 7 }}>
                        Shown to students after they complete the exam.
                      </Typography>

                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {subQuestions.length > 0 && (
              <Box sx={{ textAlign: 'right' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={handleAddSubQuestion}
                  sx={{ borderColor: '#6366f1', color: '#6366f1', textTransform: 'none' }}
                >
                  Add Sub-question
                </Button>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, pb: 1, flexWrap: 'wrap' }}>
        <EditIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        {createMode ? (createModeLabel || 'Create Question') : 'Edit Question'}
        <Chip
          label={question.type?.replace(/-/g, ' ') || 'question'}
          size="small"
          variant="outlined"
          sx={{ ml: 'auto', fontSize: '0.7rem' }}
        />
        {importId ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => setMarkdownDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Source markdown
          </Button>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>
                {localType === 'grouped' ? 'Passage / shared text — source editor' : 'Question text — source editor'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {localType === 'grouped' && (
                  <>
                    <Tooltip title="Word-wrap the passage and number every 5th line — then tweak the {L…} markers by hand">
                      <Button size="small" variant="outlined" onClick={handleDialogInsertLineNumbers} sx={{ py: 0.25, px: 1, fontSize: '0.7rem', textTransform: 'none' }}>
                        Insert line numbers
                      </Button>
                    </Tooltip>
                    <Tooltip title="Place cursor in the passage, type a line number, then click Insert to add {L#} at that position">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={dialogManualLineNum}
                          onChange={(e) => setDialogManualLineNum(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleDialogInsertManualLineNum(); }}
                          placeholder="L#"
                          inputProps={{ min: 1, max: 999, style: { padding: '2px 6px', fontSize: '0.7rem', width: 44, textAlign: 'center' } }}
                          sx={{ '& .MuiOutlinedInput-root': { height: 24 } }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleDialogInsertManualLineNum}
                          disabled={!dialogManualLineNum || parseInt(dialogManualLineNum, 10) < 1}
                          sx={{ py: 0.25, px: 0.75, fontSize: '0.7rem', textTransform: 'none', minWidth: 0 }}
                        >
                          Insert {dialogManualLineNum ? `{L${dialogManualLineNum}}` : '{L#}'}
                        </Button>
                      </Box>
                    </Tooltip>
                    {/\{L\d+\}/.test(questionText || '') && (
                      <Button size="small" variant="text" color="error" onClick={handleDialogClearLineNumbers} sx={{ py: 0.25, px: 0.75, fontSize: '0.7rem', textTransform: 'none' }}>
                        Clear
                      </Button>
                    )}
                  </>
                )}
                <Button
                  size="small"
                  variant="text"
                  startIcon={questionLivePreview ? <VisibilityOffIcon sx={{ fontSize: '1rem !important' }} /> : <VisibilityIcon sx={{ fontSize: '1rem !important' }} />}
                  onClick={() => setQuestionLivePreview((v) => !v)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {questionLivePreview ? 'Hide' : 'Show'} live preview
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
              <Box
                sx={{
                  flex: questionLivePreview ? '1 1 45%' : '1 1 100%',
                  minWidth: 0,
                  '& .quill': { background: '#fff', borderRadius: 1 },
                  '& .ql-container': {
                    minHeight: '120px',
                    fontSize: '14px',
                    borderRadius: '0 0 4px 4px',
                  },
                  '& .ql-toolbar': {
                    borderRadius: '4px 4px 0 0',
                    background: '#f9f9f9',
                    flexWrap: 'wrap',
                  },
                  '& .ql-editor': { minHeight: '120px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {localType === 'grouped' ? 'Passage / shared text (what students see)' : 'Question text (what students see)'}
                  </Typography>
                  {localType !== 'grouped' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={improving ? <CircularProgress size={12} /> : <AutoFixHigh sx={{ fontSize: '0.9rem !important' }} />}
                      onClick={handleAiImproveClick}
                      disabled={improving || generatingExplanation || createMode}
                      sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      {improving ? 'Working…' : 'AI Improve'}
                    </Button>
                  ) : null}
                </Box>
                <QuillR2Editor
                  ref={localType === 'grouped' ? passageDialogQuillRef : undefined}
                  value={questionText || ''}
                  onChange={setQuestionText}
                  onChangeSelection={localType === 'grouped' ? (range) => { if (range) savedPassageDialogSelectionRef.current = range; } : undefined}
                  modules={localType === 'grouped' ? PASSAGE_QUILL_MODULES : QUILL_MODULES}
                  formats={localType === 'grouped' ? PASSAGE_QUILL_FORMATS : QUILL_FORMATS}
                  placeholder={localType === 'grouped' ? 'Paste the full reading passage here...' : 'Question stem (formatting, lists, images…)'}
                  showMathTools
                />
              </Box>
              {questionLivePreview ? (
                <Paper variant="outlined" sx={{ flex: '1 1 45%', minWidth: { xs: '100%', md: 260 }, p: 1.5, bgcolor: 'grey.50', maxHeight: 320, overflow: 'auto', '& mjx-container': { fontSize: '0.95rem' } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Live preview (MathJax)
                  </Typography>
                  {(questionText || '').replace(/<[^>]*>/g, '').trim() ? (
                    <HtmlWithLatex html={stripTagsKeepLatex(questionText || '')} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Type or paste text to see the formatted preview.
                    </Typography>
                  )}
                </Paper>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 180 }}>
              <QuestionTypeSelector
                value={localType}
                onChange={(e) => handleLocalTypeChange(e.target.value)}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select value={difficulty} label="Difficulty" onChange={(e) => setDifficulty(e.target.value)}>
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
              </Select>
            </FormControl>
            <CategoryTreeSelect
              value={categoryId}
              onChange={setCategoryId}
              label="Category"
              noneLabel="Uncategorized"
              externalCategories={categories.length > 0 ? categories : null}
              size="small"
              sx={{ minWidth: 180 }}
            />
            {localType === 'grouped' ? (
              <TextField
                size="small"
                label="Total marks"
                type="number"
                value={groupedMarksMode === 'fixed' ? (parseInt(groupedFixedTotal, 10) || 1) : Math.max(1, sumSubQuestionMarks(subQuestions))}
                InputProps={{ readOnly: true }}
                helperText={groupedMarksMode === 'fixed' ? `Fixed (${groupedFixedTotal}/${subQuestions.length} per part)` : 'Sum of sub-question marks'}
                sx={{ width: 150 }}
              />
            ) : (
              <TextField
                size="small"
                label="Default Marks"
                type="number"
                value={defaultMarks}
                onChange={(e) => setDefaultMarks(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 130 }}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSampleQuestion}
                  onChange={(e) => setIsSampleQuestion(e.target.checked)}
                  size="small"
                />
              }
              label="Sample question"
              sx={{ alignSelf: 'center', ml: 0.5 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={mcqRecommended}
                  onChange={(e) => setMcqRecommended(e.target.checked)}
                  size="small"
                />
              }
              label="Recommend convert to MCQ (hard to answer online)"
              sx={{ alignSelf: 'center', ml: 0.5 }}
            />
            {mcqRecommended && (
              <TextField
                size="small"
                label="Note shown to students (optional)"
                value={mcqRecommendedNote}
                onChange={(e) => setMcqRecommendedNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
                sx={{ minWidth: 280, flex: 1 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 190 }} error={!subjectId}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectId ? String(subjectId) : ''}
                label="Subject"
                onChange={(e) => { setSubjectId(e.target.value ? Number(e.target.value) : null); setTopicIds([]); }}
              >
                <MenuItem value="">Not set</MenuItem>
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {subjectId
                  ? 'Drives topic tagging and the Maths-only answer-unit chips.'
                  : 'Not set — no topic tagging, and Maths answers lose their unit chip.'}
              </FormHelperText>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Passage type</InputLabel>
              <Select
                value={passageType}
                label="Passage type"
                onChange={(e) => setPassageType(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="comprehension">Comprehension</MenuItem>
                <MenuItem value="context">Context</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ minWidth: 320, flex: 1 }}>
              <TopicTreeSelect
                label="Topics"
                value={topicIds}
                onChange={(ids) => setTopicIds(ids.map(Number))}
                catalog={topicCatalog}
                yearId={topicYearId}
                onYearChange={(y) => { setTopicYearId(y); setTopicIds([]); }}
                schoolYears={schoolYears}
                disabled={!subjectId || topicCatalog.length === 0}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block' }}>
              Section (optional)
            </Typography>
            <TextField
              size="small"
              label="Section title"
              fullWidth
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="e.g. Section A"
            />
            <TextField
              size="small"
              label="Section instructions"
              fullWidth
              multiline
              minRows={2}
              value={sectionInstructions}
              onChange={(e) => setSectionInstructions(e.target.value)}
              placeholder="Instructions shown at the start of this section (optional)"
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>
                Images and diagrams (students see these with the question text)
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
              disabled={isImageProcessing}
            />
            {imageUrls.map((url, idx) => (
              <Box
                key={url}
                draggable
                onDragStart={() => setDragImageIdx(idx)}
                onDragOver={(e) => { e.preventDefault(); setDragOverImageIdx(idx); }}
                onDrop={() => { handleImageReorder(dragImageIdx, idx); setDragImageIdx(null); setDragOverImageIdx(null); }}
                onDragEnd={() => { setDragImageIdx(null); setDragOverImageIdx(null); }}
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  border: dragOverImageIdx === idx && dragImageIdx !== idx ? '2px dashed #6366f1' : '1px solid #e0e0e0',
                  borderRadius: 1,
                  overflow: 'hidden',
                  width: 88,
                  height: 88,
                  mr: 1,
                  mb: 1,
                  cursor: 'grab',
                  opacity: dragImageIdx === idx ? 0.4 : 1,
                  verticalAlign: 'top',
                }}
              >
                <img
                  src={url}
                  alt={`img-${idx}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                  onError={(e) => { e.target.style.opacity = '0.3'; }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(url)}
                  sx={{ position: 'absolute', top: 1, right: 1, bgcolor: 'rgba(255,255,255,0.85)', color: '#f44336', p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', textAlign: 'center', py: 0.2 }}>
                  <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.6rem' }}>
                    {idx === 0 ? 'Primary' : `#${idx + 1}`}
                  </Typography>
                </Box>
              </Box>
            ))}
            <Box
              onDragOver={(e) => {
                if (e.dataTransfer?.types?.includes('Files')) {
                  e.preventDefault();
                  setImageDropActive(true);
                }
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setImageDropActive(false);
              }}
              onDrop={async (e) => {
                const file = e.dataTransfer?.files?.[0];
                if (!file) return;
                e.preventDefault();
                e.stopPropagation();
                setImageDropActive(false);
                await uploadStemImageFile(file);
              }}
              onClick={() => !isImageProcessing && fileInputRef.current?.click()}
              sx={{
                mb: 1.5,
                border: '1px dashed',
                borderColor: imageDropActive ? 'primary.main' : 'divider',
                bgcolor: imageDropActive ? 'primary.50' : 'grey.50',
                borderRadius: 1.5,
                px: 2,
                py: 2,
                minHeight: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: isImageProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Drag and drop image here, or click to browse
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Larger drop zone — same upload as Add Image
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={isImageProcessing ? <CircularProgress size={14} /> : <CloudUpload />}
                onClick={(ev) => { ev.stopPropagation(); fileInputRef.current?.click(); }}
                disabled={isImageProcessing || generatingDiagram}
                sx={{ borderColor: '#6366f1', color: '#6366f1', textTransform: 'none' }}
              >
                {isImageProcessing ? 'Uploading…' : 'Add Image'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={generatingDiagram ? <CircularProgress size={14} /> : <AutoAwesome />}
                onClick={handleGenerateDiagramClick}
                disabled={isImageProcessing || generatingDiagram}
                sx={{ borderColor: '#0ea5e9', color: '#0ea5e9', textTransform: 'none' }}
              >
                {generatingDiagram ? 'Generating…' : 'Generate Diagram'}
              </Button>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Image description (what the AI should draw)"
              value={imageDescription}
              onChange={(e) => setImageDescription(e.target.value)}
              placeholder="Describe the figure exactly — layout, labels and given values. Leave empty to draw from the question text alone."
              sx={{ mt: 0.5 }}
              InputProps={{
                endAdornment: imageDescription ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setImageDescription('')} title="Delete description">
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block' }}>
                Answer Configuration
              </Typography>
              {localType !== 'grouped' ? (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                    onClick={handleConvertToGrouped}
                    title="Keep the question text as the passage/stem and add sub-questions beneath it"
                    sx={{ borderColor: '#6366f1', color: '#6366f1', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Convert to sub-questions
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={generatingAnswer ? <CircularProgress size={14} /> : <AutoAwesome />}
                    onClick={handleGenerateAnswerClick}
                    disabled={generatingAnswer || createMode}
                    sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {generatingAnswer ? 'Generating…' : 'AI Generate Answer'}
                  </Button>
                </Box>
              ) : null}
            </Box>
            {renderAnswerEditor()}
            {localType !== 'grouped' ? (() => {
              const auto = forceMeasurementTool === 'auto' ? describeMeasurementAutoDetect({ question_text: questionText, type: localType }) : null;
              const toolLabel = { angle: 'Protractor', length: 'Ruler', construct: 'Full construction set' };
              return (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 460 }}>
                  <FormControl size="small" sx={{ maxWidth: 340 }}>
                    <InputLabel>Measurement tool for student</InputLabel>
                    <Select
                      label="Measurement tool for student"
                      value={forceMeasurementTool}
                      onChange={(e) => setForceMeasurementTool(e.target.value)}
                    >
                      <MenuItem value="auto">Auto (detect from question text)</MenuItem>
                      <MenuItem value="none">None (no measurement tools)</MenuItem>
                      <MenuItem value="angle">Protractor only (angle)</MenuItem>
                      <MenuItem value="length">Ruler only (length)</MenuItem>
                      <MenuItem value="construct">Full construction set (protractor + ruler + compass + set square)</MenuItem>
                    </Select>
                  </FormControl>
                  {forceMeasurementTool === 'auto' ? (
                    <Typography variant="caption" color={auto ? 'primary.main' : 'text.secondary'}>
                      {auto
                        ? `Auto-detected: ${toolLabel[auto.mode] || auto.mode}${auto.phrase ? ` — matched "${auto.phrase}"` : ''}. Pick a tool above to override.`
                        : 'Auto: no measurement tool detected from the question text. Pick a tool above to force one.'}
                    </Typography>
                  ) : null}
                </Box>
              );
            })() : null}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                Explanation (optional)
              </Typography>
              {localType !== 'grouped' ? (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={generatingExplanation ? <CircularProgress size={12} /> : <AutoAwesome sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={handleGenerateExplanationAi}
                    disabled={generatingExplanation || improving || createMode}
                    sx={{ py: 0.25, px: 1, fontSize: '0.75rem', textTransform: 'none' }}
                  >
                    {generatingExplanation ? 'Generating…' : 'AI Generate'}
                  </Button>
                </Box>
              ) : null}
            </Box>
            <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0' }, '& .ql-container': { minHeight: 110, borderRadius: '0 0 6px 6px' } }}>
              <QuillR2Editor
                value={explanation}
                onChange={(html) => setExplanation(normalizeQuillValue(html))}
                modules={EXPL_QUILL_MODULES}
                formats={EXPL_QUILL_FORMATS}
                placeholder="Explain the correct answer for student review..."
                style={{ minHeight: 140 }}
                showMathTools
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 7 }}>
              Shown to students after they complete the exam. Use the image button to embed explanation images.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: '#666' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(buildPayload())}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', textTransform: 'none', px: 3 }}
        >
          {saving ? (createMode ? 'Creating…' : 'Saving…') : (createMode ? 'Create & add to paper' : 'Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={aiImproveOpen} onClose={() => setAiImproveOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>AI suggestion — compare and accept</DialogTitle>
      <DialogContent dividers>
        <AiAcceptSections value={aiAccept} onChange={setAiAccept} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Original</Typography>
            {aiOriginal ? (
              <>
                <QuestionPaperLayout question={aiOriginal} questionNumber={1} showAnswers showAdminHints={false} />
                <AiExplanationPreview html={aiOriginal.explanation} />
              </>
            ) : null}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'secondary.main' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main' }}>
              AI suggestion — preview of what will be applied
            </Typography>
            {aiSuggestion ? (() => {
              const merged = mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept);
              return (
                <>
                  <QuestionPaperLayout
                    question={merged}
                    questionNumber={1}
                    showAnswers
                    showAdminHints={false}
                  />
                  <AiExplanationPreview html={merged.explanation} />
                </>
              );
            })() : null}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAiImproveOpen(false)}>Dismiss</Button>
        <Button
          variant="contained"
          disabled={!aiSuggestion || !Object.values(aiAccept).some(Boolean)}
          onClick={() => {
            applyImprovedQuestionToDialog(mergeAiSuggestion(aiOriginal, aiSuggestion, aiAccept));
            setAiImproveOpen(false);
            toast.success('Applied AI suggestion — review and save');
          }}
          sx={{ textTransform: 'none' }}
        >
          Accept and apply
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={subAiImproveOpen} onClose={() => setSubAiImproveOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Sub-question AI suggestion — compare and accept</DialogTitle>
      <DialogContent dividers>
        <AiAcceptSections value={subAiAccept} onChange={setSubAiAccept} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Original</Typography>
            {subAiOriginal ? (
              <>
                <QuestionPaperLayout
                  question={subAiOriginal}
                  questionNumber={(subAiImproveIdx ?? 0) + 1}
                  showAnswers
                  showAdminHints={false}
                />
                <AiExplanationPreview html={subAiOriginal.explanation} />
              </>
            ) : null}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, border: '2px solid', borderColor: 'secondary.main' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'secondary.main' }}>
              AI suggestion — preview of what will be applied
            </Typography>
            {subAiSuggestion ? (() => {
              const merged = mergeAiSuggestion(subAiOriginal, subAiSuggestion, subAiAccept);
              return (
                <>
                  <QuestionPaperLayout
                    question={merged}
                    questionNumber={(subAiImproveIdx ?? 0) + 1}
                    showAnswers
                    showAdminHints={false}
                  />
                  <AiExplanationPreview html={merged.explanation} />
                </>
              );
            })() : null}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSubAiImproveOpen(false)}>Dismiss</Button>
        <Button
          variant="contained"
          disabled={!subAiSuggestion || subAiImproveIdx == null || !Object.values(subAiAccept).some(Boolean)}
          onClick={() => {
            const idx = subAiImproveIdx;
            const patch = applyImprovedSubQuestionPatch(
              mergeAiSuggestion(subAiOriginal, subAiSuggestion, subAiAccept)
            );
            if (idx != null && Object.keys(patch).length > 0) {
              setSubQuestions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
            }
            setSubAiImproveOpen(false);
            toast.success('Applied AI suggestion to this sub-question — review and save');
          }}
          sx={{ textTransform: 'none' }}
        >
          Accept and apply
        </Button>
      </DialogActions>
    </Dialog>

    <ImportMarkdownViewerDialog
      open={markdownDialogOpen}
      importId={importId ? Number(importId) : null}
      filename=""
      onClose={() => setMarkdownDialogOpen(false)}
    />
    </>
  );
};

export default QuestionBankQuestionEditDialog;
